/**
 * App orchestrator. Owns mode transitions (idle -> preview -> navigating),
 * wires GPS fixes into the navigator and camera, and connects the UI panels.
 */

import './style.css';
import './navigation.css';

import { APP_VERSION, hasMapbox } from './core/config';
import { isAndroidApp, isLargeAndroidDisplay, showNativeUpdateIfAvailable } from './core/runtime';
import { installAndroidConnectivityRecovery } from './core/connectivity';
import { installDriveRecovery } from './core/driveRecovery';
import { getLang, t } from './core/i18n';
import { store, select } from './core/store';
import { activeVehicle } from './core/settings';
import { UserSettingsSync } from './core/userSettingsSync';
import type { Fix, LngLat, NavProgress, Place, Route, RouteStep, Trip } from './core/types';
import { cumulativeDistances, distance, projectPointOntoLine } from './core/geo';
import { formatClock, formatDateTime, formatDistanceShort, routeLetter, setClock24 } from './core/format';
import { addRecent, listTrips, saveTrip } from './core/db';

import { GpsWatcher } from './nav/gps';
import {
  fetchRouteAlternatives,
  fetchRoutes,
  summariseRoute,
} from './nav/directions';
import { vehicleProfileKey, vehicleViolation } from './nav/vehicleConstraints';
import { fastestRouteOrder } from './nav/fastestRoute';
import { RouteNavigator } from './nav/navigator';
import { laneAdvice, resolveLanes } from './nav/lanes';
import { uturnSide } from './nav/turnSide';
import { TripRecorder } from './nav/tripRecorder';
import { computeRouteStats, statsFor, type RouteStats } from './nav/routeStats';
import { applyPace, paceDuration, tierForStep } from './nav/paceModel';
import { planFerry } from './nav/ferry';
import {
  adviseOnTraffic,
  congestedShare,
  overlapShare,
  pickLowTraffic,
  sampleRemaining,
  trafficDelay,
} from './nav/trafficWatch';
import {
  fastRoadSeconds,
  motorwaySeconds,
  roadWidthProfile,
  type RoadWidthProfile,
} from './nav/bigRoads';
import { roadLabel } from './nav/roads';
import { learnedVias } from './nav/learnedRoutes';
import {
  buildRoughIndex,
  roughnessOfRoute,
  roughAhead,
  type RoughIndex,
  type RouteRoughness,
} from './nav/roughMap';
import { RoughnessMeter } from './nav/roughness';
import { VehicleHealthMonitor } from './nav/vehicleHealth';
import { BreadcrumbTrail } from './nav/breadcrumbs';
import { clearActiveTrip, loadActiveTrip, rememberActiveTrip } from './nav/activeTripMemory';
import { readPoint } from './nav/tripRecorder';
import { enableWakeLock, disableWakeLock } from './nav/wakelock';
import { reverseGeocode } from './search/geocode';
import { clearSharedPlaceImportUrl, readSharedPlaceImport } from './search/shareTarget';
import { adviceKey, fetchAdvice, type RouteAdvice } from './ai/advisor';
import { routeChoices, viasForPicks, type LegPick } from './nav/routeSplit';
import { AskAiButton } from './ui/askAi';
import { HotelSheet, type HotelTab } from './ui/hotelSheet';
import { findHotelsAhead, findHotelsNear, type Hotel } from './nav/hotels';
import { Fleet, type MessageKind, type QuickMessage } from './fleet/fleet';
import { findFuelStopsAhead, etaToStop, pttGapStats, pttWithSevenEleven, type FuelStop, type PttGapStats } from './nav/fuelStops';
import { LaneGuideCache } from './ai/laneAdvisor';
import { scanRouteClearances, getRouteClearance, type RouteClearance } from './nav/clearance';
import { analyseElevation, type ElevationProfile } from './nav/elevation';
import { scanManeuverStructures } from './nav/uturnStructure';

import { MapView } from './map/mapView';
import { ManeuverCard } from './ui/maneuverCard';
import { NavHud } from './ui/navHud';
import { SearchPanel } from './ui/searchPanel';
import { RoutePreview } from './ui/routePreview';
import { SettingsPanel, type AppUpdateResult } from './ui/settingsPanel';
import { mirrorLangForWorker } from './pwa/push';
import { createIdleActionGate } from './pwa/updateGate';
import { TripsPanel } from './ui/tripsPanel';

import { CarDock } from './ui/carDock';
import { FleetBubble } from './ui/fleetBubble';
import { FuelBubble, type FuelChoice } from './ui/fuelBubble';
import { VehiclePicker } from './ui/vehiclePicker';
import { RouteInfoSheet } from './ui/routeInfo';
import { LiveDataPanel } from './ui/liveData';
import { QuickSettings } from './ui/quickSettings';
import { RouteOffer } from './ui/routeOffer';

import { announce, primeSpeech, stopSpeaking } from './audio/voice';



import { LoginView } from './auth/loginView';
import * as authApi from './auth/api';
import type { SavedPlace, User } from './auth/api';

const appEl = document.getElementById('app')!;
const uiEl = document.getElementById('ui')!;
const mapEl = document.getElementById('map')!;

// Resolve the shell before authentication or any asynchronous startup work.
// Workbox has one navigation fallback, so a controlled /car/ visit may receive
// the root HTML shell. Repointing VitePWA's single manifest link here preserves
// the car install identity without maintaining two app bundles or relying on
// HTML link order.
const androidApp = isAndroidApp();
if (androidApp) installAndroidConnectivityRecovery();
// The dashboard wakes before its Wi-Fi surprisingly often. The normal API
// deadline is intentionally generous, but holding the entire car UI behind it
// makes ignition startup look dead. A stored approved session remains the
// offline fallback in authApi.me(), so fail over quickly only in the APK.
const CAR_STARTUP_AUTH_TIMEOUT_MS = 2_500;
const CAR_STARTUP_STYLE_TIMEOUT_MS = 3_000;
const explicitCarMode =
  /\/car\/?$/.test(location.pathname) || new URLSearchParams(location.search).has('car');
// Entry point controls trip synchronization and install identity, never layout.
const carMode = explicitCarMode || isLargeAndroidDisplay();
const dashboardMQ = window.matchMedia('(min-width: 700px)');
let dashboardMode = dashboardMQ.matches;

function applyAppManifest(useCarManifest: boolean): void {
  const links = [...document.head.querySelectorAll<HTMLLinkElement>('link[rel="manifest"]')];
  const link = links.shift() ?? document.createElement('link');
  link.rel = 'manifest';
  link.href = useCarManifest ? '/manifest-car.webmanifest' : '/manifest.webmanifest';
  if (!link.isConnected) document.head.appendChild(link);
  for (const duplicate of links) duplicate.remove();
}

applyAppManifest(carMode);
appEl.classList.toggle('app--car', dashboardMode);
appEl.classList.toggle('app--android', androidApp);
const refreshShellClass = () => appEl.classList.toggle('app--car', dashboardMQ.matches);
if (typeof dashboardMQ.addEventListener === 'function') dashboardMQ.addEventListener('change', refreshShellClass);
else dashboardMQ.addListener(refreshShellClass);

/**
 * Startup must never fail silently to a black screen. Any uncaught error
 * before the UI exists gets rendered full-screen so the failure is readable
 * on a phone or head unit with no devtools.
 */
function fatalScreen(title: string, detail: string): void {
  uiEl.innerHTML = `<div class="fatal"><h1>${title}</h1><p>${detail}</p>
    <button class="btn btn--primary" data-act="reload">Reload</button></div>`;
  uiEl.querySelector('[data-act=reload]')?.addEventListener('click', () => location.reload());
}

let bootFinished = false;
let bootStartedAt = 0;

/**
 * Startup breadcrumbs, made impossible to miss: every step is mirrored into
 * the tab title and a small on-screen strip. On a healthy boot the strip
 * exists for under a second; on a stall it stays, telling us the exact step.
 */
const bootTrace: string[] = [];
let traceEl: HTMLElement | null = null;

function trace(step: string): void {
  bootTrace.push(step);
  document.title = `WF·${step}`;
  if (!traceEl) {
    traceEl = document.createElement('div');
    traceEl.className = 'boot-trace';
    document.body.appendChild(traceEl);
  }
  traceEl.textContent = bootTrace.join(' → ');
}

function traceDone(): void {
  bootFinished = true;
  document.title = 'Wayfarer Navigation';
  traceEl?.remove();
  traceEl = null;
}

function traceText(): string {
  return `Progress: ${bootTrace.join(' → ') || '(nothing ran)'}`;
}

// Watch continuously: once boot() has started, it must finish or error within
// 15 s - otherwise show the stall screen with the breadcrumbs.
window.setInterval(() => {
  if (!bootFinished && bootStartedAt && Date.now() - bootStartedAt > 15_000 && !document.querySelector('.fatal')) {
    fatalScreen('Startup stalled', traceText());
  }
}, 5_000);

/**
 * Keep every device on the latest deploy.
 *
 * This used to set a one-shot `wf-sw-reloaded` flag: the app reloaded for the
 * FIRST service-worker takeover of a tab session and never again. A phone
 * running this as a PWA keeps the same session alive for days, so the second
 * and every later deploy was fetched and then ignored - the driver kept
 * running an old bundle and reported bugs that had already been fixed.
 *
 * A waiting worker is activated only while idle. Its later controller takeover
 * is gated again before reload because the driver can open search or start a
 * route in the small gap between those two events. A long-lived PWA may never
 * navigate, so idle clients still poll on resume and on a timer.
 */
let checkForAppUpdate: () => Promise<AppUpdateResult> = async () =>
  'serviceWorker' in navigator ? 'failed' : 'unavailable';

if ('serviceWorker' in navigator) {
  let registration: ServiceWorkerRegistration | null = null;

  const reloadGate = createIdleActionGate(store.get().mode, () => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem('wf-sw-reloaded') ?? 0);
    } catch {
      // Private/locked-down head units can disable storage. Updating must still
      // work there; the active-worker change itself is the loop guard.
    }
    if (Date.now() - last > 10_000) {
      try {
        sessionStorage.setItem('wf-sw-reloaded', String(Date.now()));
      } catch {
        /* storage unavailable - continue with the one required reload */
      }
      location.reload();
    }
  });

  const activationGate = createIdleActionGate(store.get().mode, () => {
    const waiting = registration?.waiting;
    if (!waiting) throw new Error('The waiting service worker is no longer available.');
    waiting.postMessage({ type: 'SKIP_WAITING' });
  });

  store.subscribe((state) => {
    activationGate.setMode(state.mode);
    reloadGate.setMode(state.mode);
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => reloadGate.request());

  const swUrl = new URL(`${import.meta.env.BASE_URL}sw.js`, location.origin).toString();
  void navigator.serviceWorker
    .register(swUrl, { scope: import.meta.env.BASE_URL, updateViaCache: 'none' })
    .then((reg) => {
      registration = reg;

      checkForAppUpdate = async (): Promise<AppUpdateResult> => {
        try {
          await reg.update();
          const installing = reg.installing;
          if (installing && !reg.waiting) {
            await new Promise<void>((resolve) => {
              let settled = false;
              const finish = (): void => {
                if (settled) return;
                settled = true;
                clearTimeout(timeout);
                resolve();
              };
              const timeout = window.setTimeout(finish, 12_000);
              installing.addEventListener('statechange', () => {
                if (['installed', 'activated', 'redundant'].includes(installing.state)) finish();
              });
            });
          }
          if (reg.waiting && navigator.serviceWorker.controller) {
            const idle = store.get().mode === 'idle';
            activationGate.request();
            return idle ? 'ready' : 'deferred';
          }
          return 'current';
        } catch {
          return 'failed';
        }
      };

      const queueWaitingUpdate = (): void => {
        if (reg.waiting && navigator.serviceWorker.controller) activationGate.request();
      };
      queueWaitingUpdate();
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            // Let Registration.waiting settle before the idle action reads it.
            setTimeout(queueWaitingUpdate, 0);
          }
        });
      });

      const checkWhenIdle = (): void => {
        if (store.get().mode !== 'idle') return;
        if (reg.waiting) activationGate.request();
        else void reg.update().catch(() => {});
      };
      setInterval(checkWhenIdle, 15 * 60_000);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) checkWhenIdle();
      });
    })
    .catch((err) => console.warn('[pwa] service worker registration failed', err));
}

window.addEventListener('error', (e) => {
  if (!bootFinished) {
    fatalScreen(
      'Wayfarer could not start',
      `${String(e.error?.message ?? e.message)}<br/><small>${traceText()}</small>`,
    );
  }
});
window.addEventListener('unhandledrejection', (e) => {
  // Background services must not destroy the UI while boot is still running.
  // The actual gate/boot promises have their own fatal handler below.
  console.warn('[background]', e.reason);
});

/** Mapbox GL v3 needs WebGL2; without it (hardware acceleration off, old GPU
 *  driver, some head units) the Map constructor throws. Check up front so the
 *  driver gets an actionable message instead of a black screen. */
function webgl2Available(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2');
    context?.getExtension('WEBGL_lose_context')?.loseContext();
    return context !== null;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------------- */
/* Auth gate                                                                  */
/* ------------------------------------------------------------------------- */

let currentUser: User | null = null;
const devVisualFixture =
  import.meta.env.DEV &&
  ['junctionDemo', 'uturnDemo', 'turnDemo', 'turnPairDemo', 'laneDemo', 'tollDemo'].some((key) =>
    new URLSearchParams(location.search).has(key),
  );

async function gate(): Promise<void> {
  trace('gate');
  if (!hasMapbox()) {
    uiEl.innerHTML = `<div class="fatal">
      <h1>Wayfarer</h1>
      <p>No Mapbox token configured. Copy <code>.env.example</code> to <code>.env.local</code>,
      paste your free token from account.mapbox.com, and rebuild.</p></div>`;
    return;
  }
  // Visual fixtures exercise the real card/layout code but must not depend on
  // a developer browser carrying a live account session. This branch is
  // removed from production builds with the rest of the demo-only code.
  if (devVisualFixture) {
    trace('dev:visual-fixture');
    currentUser = { id: 0, name: 'Visual fixture', role: 'driver', status: 'approved' };
    void boot().catch(bootFailed);
    return;
  }
  try {
    const { user } = await authApi.me(
      AbortSignal.timeout(CAR_STARTUP_AUTH_TIMEOUT_MS),
    );
    trace(user ? `me:${user.status}` : 'me:none');
    if (user && user.status === 'approved') {
      currentUser = user;
      void boot().catch(bootFailed);
      return;
    }
  } catch {
    /* no backend reachable - fall through to login, which will show the error */
  }
  trace('login:shown');
  const login = new LoginView(uiEl, (user) => {
    trace('login:authed');
    currentUser = user;
    login.destroy();
    void boot().catch(bootFailed);
  });
}

/* ------------------------------------------------------------------------- */
/* Main app                                                                   */
/* ------------------------------------------------------------------------- */

async function boot(): Promise<void> {
  bootStartedAt = Date.now();
  trace('boot');
  if (!webgl2Available()) {
    fatalScreen(
      'Map engine unavailable',
      'This browser has no WebGL2, which the map needs. Usually this means ' +
        'hardware acceleration is turned OFF: open Chrome Settings → System → ' +
        'enable "Use graphics acceleration when available", relaunch the browser, ' +
        'and reload this page. (chrome://gpu shows the current state.)',
    );
    return;
  }

  // Resolve the authenticated user's settings before constructing anything
  // that reads them (map language/theme, camera, voice and route policy). The
  // sync applies its local per-user cache immediately and bounds the initial
  // server wait, so an offline car still starts normally.
  if (currentUser) {
    trace('settings:sync');
    await new UserSettingsSync(currentUser.id).start();
    trace('settings:ok');
  }

  /* ------------------------------ theme ------------------------------- */
  const resolveTheme = (): 'night' | 'day' => {
    const pref = store.get().settings.theme;
    if (pref !== 'auto') return pref;
    const h = new Date().getHours() + new Date().getMinutes() / 60;
    return h >= 6.25 && h < 18.5 ? 'day' : 'night';
  };

  // Keep this tied to state rather than only to start/end navigation. That
  // makes changing the setting mid-drive take effect immediately.
  select(
    (st) => st.mode === 'navigating' && st.settings.keepAwake,
    (keepAwake) => (keepAwake ? enableWakeLock() : disableWakeLock()),
  );

  /* ------------------------------- map -------------------------------- */
  trace('webgl:ok');
  const { loadStrippedStyle } = await import('./map/styleLoader');
  trace('style:fetch');
  // Thai UI gets a Thai map: the style is rewritten to prefer each place's own
  // name over the English one. See map/styleLoader.ts - there is no name_th
  // field to switch to, so this works by no longer overriding `name`.
  const labelLang: 'en' | 'local' = getLang() === 'th' ? 'local' : 'en';
  const bootTheme = resolveTheme();
  const bootStyle = await loadStrippedStyle(
    bootTheme,
    labelLang,
    CAR_STARTUP_STYLE_TIMEOUT_MS,
  );
  trace(typeof bootStyle === 'string' ? 'style:fallback' : 'style:ok');
  let openFleetDriver = (_id: number): void => {};
  const mapView = new MapView(
    mapEl,
    bootStyle,
    {
      onLongPress: (point) => void pickDroppedPin(point),
      onAltRouteClick: (routeId) => {
        const idx = store.get().routes.findIndex((r) => r._id === routeId);
        if (idx >= 0) selectRoute(idx);
      },
      onUserGesture: () => {
        // Breaking follow does not by itself warrant the button - it appears
        // once the vehicle has actually drifted off screen.
        refreshRecenter();
      },
      onPlaceClick: (marker) => {
        if (store.get().mode === 'navigating') return;
        void pickDestination({
          id: `saved:${marker.id}`,
          name: marker.name,
          address: `Saved by ${marker.creatorName}`,
          center: [marker.lng, marker.lat],
          kind: 'saved',
        });
      },
      onFleetDriverClick: (id) => openFleetDriver(id),
    },
    bootTheme,
    true,
  );
  mapView.setLabelLanguage(labelLang);
  mapView.setBuildings3d(store.get().settings.buildings3d);
  select(
    (s) => s.settings.buildings3d,
    (on) => mapView.setBuildings3d(on),
    { immediate: false },
  );
  const applyCameraPrefs = () => {
    const s = store.get().settings;
    mapView.setCameraPrefs({ pitch: s.cameraPitch, driveZoom: s.driveZoom });
  };
  applyCameraPrefs();
  select((s) => s.settings.cameraPitch, applyCameraPrefs, { immediate: false });
  select((s) => s.settings.driveZoom, applyCameraPrefs, { immediate: false });


  trace('map:ok');
  /* -------------------------- shared saved places ----------------------- */
  let sharedPlaces: SavedPlace[] = [];

  async function loadPlaces(): Promise<void> {
    try {
      sharedPlaces = (await authApi.fetchPlaces()).places;
      mapView.setPlaces(sharedPlaces);
    } catch {
      /* offline or backend down - markers just don't refresh */
    }
  }
  void loadPlaces();
  // Shared locations from other drivers should appear without a reload.
  setInterval(() => void loadPlaces(), 90_000);

  function isDestSaved(): boolean {
    const dest = store.get().destination;
    if (!dest) return false;
    return sharedPlaces.some((p) => distance([p.lng, p.lat], dest.center) < 30);
  }

  let lastTheme = bootTheme;
  setInterval(() => {
    const t = resolveTheme();
    if (t !== lastTheme) {
      lastTheme = t;
      void mapView.setTheme(t);
    }
  }, 60_000);
  select(
    (s) => s.settings.theme,
    () => {
      const t = resolveTheme();
      if (t !== lastTheme) {
        lastTheme = t;
        void mapView.setTheme(t);
      }
    },
    { immediate: false },
  );

  /* -------------------------------- UI --------------------------------- */
  /*
   * No approach beep (operator, 2026-08-15: "remove the beep too").
   *
   * The 1 km / 200 m alert used to sound one pip alongside the card flash.
   * The flash carries the same information silently, and the pip interrupted
   * whatever was playing twice per turn for the whole drive.
   */
  const maneuverCard = new ManeuverCard(uiEl);
  // /car gives the follow-up maneuver and lane arrows their own map positions.
  // Everywhere else both stay inside the main instruction card.
  maneuverCard.setDetachedThen(dashboardMode);

  const detachedThenEl = uiEl.querySelector<HTMLElement>('.maneuver-card__then--detached');

  /*
   * Keep the right-hand control rail below the sign.
   *
   * The card grows and shrinks - lane chips, a lane-trap warning, a second
   * road on the strip - so any fixed offset for the buttons beside it is wrong
   * some of the time. Measuring it is the only version that cannot overlap.
   */
  function measureRail(): void {
    // Measured in landscape too: there the sign heads the left panel and the
    // fuel/fleet cards stack beneath it, so the rail must clear it as well.
    const cardRect = maneuverCard.el.hidden ? null : maneuverCard.el.getBoundingClientRect();
    const thenRect = detachedThenEl?.hidden ? null : detachedThenEl?.getBoundingClientRect();
    const h = cardRect?.height ?? 0;
    const carGutter = Number.parseFloat(getComputedStyle(appEl).getPropertyValue('--car-gutter')) || 0;
    // Changing --card-h moves the detached card without resizing it, so a
    // ResizeObserver does not necessarily fire a second time for its new
    // position. Derive its bottom from the just-measured main card as well as
    // accepting the current rectangle; the larger value is always safe.
    const derivedThenBottom = thenRect ? (cardRect?.bottom ?? 0) + carGutter + thenRect.height : 0;
    const clearTop = Math.max(cardRect?.bottom ?? 0, thenRect?.bottom ?? 0, derivedThenBottom);
    document.documentElement.style.setProperty('--card-h', `${Math.round(h)}px`);
    // Expanded media is allowed to use the map, but never the sign or its
    // detached follow-up instruction. This is a viewport coordinate because
    // sign scaling changes its visual bottom without changing normal flow.
    document.documentElement.style.setProperty('--nav-clear-top', `${Math.ceil(clearTop)}px`);
  }
  const railObserver = new ResizeObserver(measureRail);
  railObserver.observe(maneuverCard.el);
  if (detachedThenEl) railObserver.observe(detachedThenEl);
  // CSS transforms do not change ResizeObserver's layout box. Re-measure at
  // the end of the sign-scale animation so the rail clears the painted card,
  // not the size it had on the first frame of the transition.
  maneuverCard.el.addEventListener('transitionend', (event) => {
    if (event.propertyName === 'transform') measureRail();
  });
  window.addEventListener('resize', measureRail);
  window.addEventListener('orientationchange', () => setTimeout(measureRail, 250));
  measureRail();

  const hud = new NavHud(uiEl, {
    onEnd: () => endNavigation('ended'),
    onRecenter: () => {
      // Hand it the position to go to - without one it can only raise the
      // follow flag, which does nothing at all outside navigation.
      const fix = store.get().fix;
      mapView.recenter(fix ? [fix.lng, fix.lat] : null);
      hud.setRecenterVisible(false);
      // Re-check once the ease has landed: if the vehicle is somehow still off
      // screen the button must come back rather than leave the driver stranded.
      window.setTimeout(refreshRecenter, 900);
    },
    onOverview: () => {
      const route = activeRoute();
      if (route) mapView.fitRoute([route], isPortrait(), 0, 0);
      window.setTimeout(refreshRecenter, 800);
    },
  });
  const routeOffer = new RouteOffer(uiEl);

  const idleBar = document.createElement('div');
  idleBar.className = 'idlebar';
  idleBar.innerHTML = `
    <div class="idlebar__status" hidden></div>
    <div class="idlebar__buttons">
      <button class="icon-btn idlebar__btn" data-act="trips" aria-label="Trips">
        <svg viewBox="0 0 24 24"><path d="M4 19 C4 12 10 12 12 8 C13 6 12 4 12 4 M12 4 L9 6 M12 4 L15 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="5" cy="19" r="2" fill="currentColor"/><circle cx="19" cy="5" r="2" fill="currentColor"/></svg>
      </button>
      <button class="icon-btn idlebar__btn" data-act="share" aria-label="Share my location">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2" fill="currentColor"/><path d="M4.5 12 a7.5 7.5 0 0 1 15 0 M7.5 12 a4.5 4.5 0 0 1 9 0" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
      </button>
      <button class="icon-btn idlebar__btn" data-act="settings" aria-label="Settings">
        <svg viewBox="0 0 24 24"><path d="M12 8.5 a3.5 3.5 0 1 0 0 7 a3.5 3.5 0 0 0 0-7 Z M19 12 c0-.6.5-1.2.4-1.8 l-1.8-.6 -.5-1.2 .9-1.7 c-.7-.8-1.2-1.2-2-1.7 l-1.6 1 -1.3-.5 -.7-1.8 h-2.4 l-.7 1.8 -1.3.5 -1.6-1 c-.8.5-1.3 1-2 1.7 l.9 1.7 -.5 1.2 -1.8.6 c-.1.6.4 1.2.4 1.8 s-.5 1.2-.4 1.8 l1.8.6 .5 1.2 -.9 1.7 c.7.8 1.2 1.2 2 1.7 l1.6-1 1.3.5 .7 1.8 h2.4 l.7-1.8 1.3-.5 1.6 1 c.8-.5 1.3-1 2-1.7 l-.9-1.7 .5-1.2 1.8-.6 c.1-.6-.4-1.2-.4-1.8 Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>
      </button>
    </div>
    <button class="fab" data-act="search" aria-label="Search destination">
      <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2.4"/><path d="M15.5 15.5 L21 21" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
      <span>${t('idle.whereTo')}</span>
    </button>`;
  uiEl.appendChild(idleBar);



  /**
   * Trips are not always planned from where you are standing - the driver may
   * be costing a run that starts at the depot tomorrow morning. When
   * `plannedOrigin` is set the preview routes from there instead of the GPS
   * fix; pressing Start still re-routes from the real position, because
   * turn-by-turn has to begin where the vehicle actually is.
   */
  let plannedOrigin: Place | null = null;
  let searchTarget: 'origin' | 'destination' | 'stop' = 'destination';
  let routeStops: Place[] = [];
  /** The destination preview was opened from a still-live search result list. */
  let returnToSearchResults = false;

  async function setPlannedOrigin(place: Place | null): Promise<void> {
    plannedOrigin = place;
    searchTarget = 'destination';
    setPanel(null);
    await refetchPreviewRoutes();
  }

  const searchPanel = new SearchPanel(
    uiEl,
    () => currentFixLngLat(),
    () =>
      searchTarget === 'stop'
        ? activeRoute()?.geometry.coordinates ?? null
        : null,
    (place) => {
      if (searchTarget === 'origin') void setPlannedOrigin(place);
      else if (searchTarget === 'stop') void addRouteStop(place);
      else void pickDestination(place, true, true);
    },
    () => {
      returnToSearchResults = false;
      if (store.get().mode === 'preview') searchTarget = 'destination';
      setPanel(null);
    },
    () => sharedPlaces,
    async (id) => {
      await authApi.deletePlace(id);
      await loadPlaces();
    },
    async (place) => {
      const name = await openNameDialog(t('place.rename'), place.name);
      if (name == null || name === place.name) return;
      try {
        await authApi.renamePlace(place.id, name);
      } catch (err) {
        alert(err instanceof Error ? err.message : t('place.saveFailed'));
        return;
      }
      await loadPlaces();
    },
    async () => {
      await loadPlaces();
    },
  );

  const preview = new RoutePreview(uiEl, {
    onGo: startNavigation,
    onCancel: cancelPreview,
    onSelectRoute: selectRoute,
    onPickLeg: (choiceIndex, routeIndex) => void pickLeg(choiceIndex, routeIndex),
    onSwitchVehicle: () => {
      // Was a blind cycle to the next vehicle, which never answered "which one
      // am I routing as right now". Now it opens the list with the active one
      // ticked - see ui/vehiclePicker.ts.
      const s = store.get().settings;
      vehiclePicker.open(s.vehicles, s.activeVehicleId);
    },
    onSavePlace: () => {
      const dest = store.get().destination;
      if (!dest) return;
      openSaveDialog(dest.name, dest.center);
    },
    onAvoidTraffic: () => {
      const s = store.get();
      const pick = pickLowTraffic(s.routes, routeStats);
      if (pick === null) return;
      if (pick === s.selectedRouteIndex) {
        statusFlash(t('traffic.already'));
        return;
      }
      selectRoute(pick);
      statusFlash(t('traffic.picked', { letter: routeLetter(pick) }));
    },
    onPickOrigin: () => {
      returnToSearchResults = false;
      searchTarget = 'origin';
      setPanel('search');
    },
    onClearOrigin: () => void setPlannedOrigin(null),
    onDismissAdvice: () => {
      advice = null;
      adviceDismissed = true;
      renderPreview();
    },
    canReturnToSearch: () => returnToSearchResults,
    onAddStop: () => {
      if (routeStops.length >= 8) {
        statusFlash(t('route.maxStops'));
        return;
      }
      returnToSearchResults = false;
      searchTarget = 'stop';
      setPanel('search');
    },
    onRemoveStop: (index) => void removeRouteStop(index),
    onMoveStop: (index, delta) => void moveRouteStop(index, delta),
  });

  /* --------------------------- save place dialog ------------------------ */

  /**
   * Minimal name prompt, styled like the save dialog. Used when sharing the
   * current position - "add a popup to save the name of the place, instead of
   * editing later" - and when renaming an existing saved place. Resolves with
   * the chosen name, or null on cancel.
   */
  function openNameDialog(title: string, prefill: string): Promise<string | null> {
    return new Promise((resolve) => {
      const dialog = document.createElement('div');
      dialog.className = 'save-dialog';
      dialog.innerHTML = `
        <div class="save-dialog__card">
          <h3>${escapeText(title)}</h3>
          <input class="save-dialog__name" type="text" maxlength="120"
                 placeholder="${t('save.name.ph')}" value="${prefill.replace(/"/g, '&quot;')}"/>
          <div class="save-dialog__actions">
            <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
            <button class="btn btn--primary" data-act="save">${t('save.save')}</button>
          </div>
        </div>`;
      uiEl.appendChild(dialog);

      const input = dialog.querySelector<HTMLInputElement>('.save-dialog__name')!;
      // Select the prefill so typing replaces it in one motion.
      setTimeout(() => {
        input.focus();
        input.select();
      }, 60);

      const finish = (value: string | null) => {
        dialog.remove();
        resolve(value);
      };
      dialog.querySelector('[data-act=cancel]')!.addEventListener('click', () => finish(null));
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) finish(null);
      });
      const save = () => finish(input.value.trim() || prefill);
      dialog.querySelector('[data-act=save]')!.addEventListener('click', save);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') save();
      });
    });
  }
  /** Folder names are user text - escape before they reach markup. */
  function escapeAttr(s: string): string {
    return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function openSaveDialog(prefill: string, center: LngLat): void {
    const categories: Array<{ id: authApi.PlaceCategory; label: string }> = [
      { id: 'home', label: t('cat.home') },
      { id: 'work', label: t('cat.work') },
      { id: 'showroom', label: t('cat.showroom') },
      { id: 'depot', label: t('cat.depot') },
      { id: 'other', label: t('cat.other') },
    ];
    let selected: authApi.PlaceCategory = 'other';
    /*
     * File it at the moment of saving.
     *
     * Saving and then hunting the place down in the list to file it is two jobs
     * for one intention - and the moment you know which folder a customer
     * belongs in is the moment you save them. The chooser is populated from the
     * fleet's folders; picking "New folder" names one on the spot, and the
     * server creates it as part of filing.
     */
    let folder: string | null = null;

    const dialog = document.createElement('div');
    dialog.className = 'save-dialog';
    dialog.innerHTML = `
      <div class="save-dialog__card">
        <h3>${t('save.title')}</h3>
        <input class="save-dialog__name" type="text" maxlength="120"
               placeholder="${t('save.name.ph')}" value="${prefill.replace(/"/g, '&quot;')}"/>
        <div class="save-dialog__cats">
          ${categories
            .map(
              (c) =>
                `<button class="chip ${c.id === selected ? 'is-on' : ''}" data-cat="${c.id}">${c.label}</button>`,
            )
            .join('')}
        </div>
        <div class="save-dialog__folder">
          <span class="save-dialog__folder-label">${t('folder.saveInto')}</span>
          <div class="save-dialog__folders"></div>
        </div>
        <div class="save-dialog__actions">
          <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
          <button class="btn btn--primary" data-act="save">${t('save.save')}</button>
        </div>
      </div>`;
    uiEl.appendChild(dialog);

    const nameInput = dialog.querySelector<HTMLInputElement>('.save-dialog__name')!;

    // Folder chips, filled in once the list arrives - the dialog opens straight
    // away rather than waiting on the network to show a name field.
    const folderBox = dialog.querySelector<HTMLElement>('.save-dialog__folders')!;
    const renderFolders = (names: string[]): void => {
      folderBox.innerHTML =
        `<button class="chip ${folder === null ? 'is-on' : ''}" data-folder="">${t('folder.none')}</button>` +
        names
          .map(
            (f) =>
              `<button class="chip ${folder === f ? 'is-on' : ''}" data-folder="${escapeAttr(f)}">${escapeAttr(f)}</button>`,
          )
          .join('') +
        `<button class="chip chip--new" data-folder-new="1">+ ${t('folder.new')}</button>`;
      folderBox.querySelectorAll<HTMLButtonElement>('[data-folder]').forEach((b) =>
        b.addEventListener('click', () => {
          folder = b.dataset.folder || null;
          renderFolders(names);
        }),
      );
      folderBox.querySelector<HTMLElement>('[data-folder-new]')?.addEventListener('click', () => {
        const made = prompt(t('folder.newPrompt'))?.trim();
        if (!made) return;
        folder = made;
        renderFolders(names.includes(made) ? names : [...names, made]);
      });
    };
    renderFolders([]);
    void authApi
      .fetchFolders()
      .then(({ folders }) => renderFolders(folders))
      .catch(() => {
        /* offline: "No folder" and "New folder" still work */
      });
    dialog.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach((b) =>
      b.addEventListener('click', () => {
        selected = b.dataset.cat as authApi.PlaceCategory;
        dialog.querySelectorAll('[data-cat]').forEach((x) => x.classList.toggle('is-on', x === b));
        // Convenience: picking a category with the default name pre-fills it.
        if (!nameInput.value.trim() || nameInput.value === prefill) {
          const cat = categories.find((c) => c.id === selected);
          if (cat && selected !== 'other') nameInput.value = cat.label;
        }
      }),
    );
    dialog.querySelector('[data-act=cancel]')!.addEventListener('click', () => dialog.remove());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.remove();
    });
    dialog.querySelector('[data-act=save]')!.addEventListener('click', () => {
      const name = nameInput.value.trim() || prefill;
      void authApi
        .createPlace(name, center[0], center[1], 'place', selected, folder)
        .then(async () => {
          dialog.remove();
          await loadPlaces();
          renderPreview();
        })
        .catch((err) => alert(err instanceof Error ? err.message : t('place.saveFailed')));
    });
  }

  const settingsPanel = new SettingsPanel(
    uiEl,
    () => currentUser,
    () => {
      setPanel(null);
      // The avoid-tolls / avoid-motorways toggles live here now, so closing
      // settings has to re-run the search the way the on-sheet chips used to.
      // Otherwise the preference is saved and the routes on screen ignore it.
      if (store.get().mode === 'preview') void refetchPreviewRoutes();
    },
    () => {
      void authApi.logout().finally(() => location.reload());
    },
    async () => {
      const [webResult, nativeReady] = await Promise.all([
        checkForAppUpdate(),
        showNativeUpdateIfAvailable(uiEl),
      ]);
      return nativeReady ? 'native' : webResult;
    },
  );

  const quickSettings = new QuickSettings(
    uiEl,
    () => store.get().settings,
    (patch) => store.patchSettings(patch),
    () => setPanel('settings'),
  );

  const tripsPanel = new TripsPanel(
    uiEl,
    () => setPanel(null),
    (trip) => {
      setPanel(null);
      void pickDestination({
        id: `trip:${trip.id}`,
        name: trip.destinationName,
        address: '',
        center: trip.destination,
        kind: 'history',
      });
    },
    (trip) => {
      setPanel(null);
      showTripOnMap(trip);
    },
  );

  /*
   * A recorded trip, laid back on the map.
   *
   * The track is stored with time and speed per point precisely so questions
   * like "how fast did we go through there" stay answerable after the fact:
   * tap the drawn line and the popup shows the recorded clock time and speed
   * at that spot, with one tap more to navigate back to it. The floating chip
   * is the way out - without it the coloured line looks like a stuck overlay.
   */
  const tripViewChip = document.createElement('button');
  tripViewChip.className = 'trip-view-chip';
  tripViewChip.hidden = true;
  uiEl.appendChild(tripViewChip);
  tripViewChip.addEventListener('click', () => clearTripView());

  function clearTripView(): void {
    mapView.clearTripTrack();
    tripViewChip.hidden = true;
  }

  function showTripOnMap(trip: Trip): void {
    const points = (trip.track ?? []).map(readPoint).filter((p) => p[0] !== 0 || p[1] !== 0);
    if (points.length < 2) return;
    const units = store.get().settings.units;
    mapView.showTripTrack(points, {
      label: (p) => {
        const kmh = p[3];
        const shown = units === 'imperial' ? Math.round(kmh / 1.609344) : Math.round(kmh);
        const unit = units === 'imperial' ? 'mph' : 'km/h';
        return `<strong>${shown} ${unit}</strong> · ${formatClock(p[2])}<br><small>${formatDateTime(p[2])}</small>`;
      },
      navLabel: t('trips.goHere'),
      onNavigate: (at) => {
        clearTripView();
        void pickDroppedPin(at);
      },
    });
    tripViewChip.hidden = false;
    tripViewChip.innerHTML = `✕ <span>${escapeText(trip.destinationName)} · ${formatDateTime(trip.startedAt)}</span>`;
  }



  /**
   * Re-centre control.
   *
   * Shown only while actually navigating, and only once the vehicle has
   * genuinely left the screen - not the instant the map is nudged. A button
   * that appears on every small pan is noise, and one that appears when there
   * is no route to return to is meaningless. The HUD owns the control itself;
   * this only decides when it is warranted.
   */
  const forceRecenterDemo = import.meta.env.DEV && new URLSearchParams(location.search).has('recenterDemo');
  function refreshRecenter(): void {
    if (forceRecenterDemo) {
      hud.setRecenterVisible(true);
      return;
    }
    const st = store.get();
    // Shown whenever the vehicle has left the screen - with or without a
    // route. Panning away from yourself while parked is just as disorienting
    // as doing it mid-drive, and it was the same button either way.
    if (!st.fix || st.mode === 'preview') {
      hud.setRecenterVisible(false);
      return;
    }
    const strayed = !mapView.isFollowing && mapView.vehicleOffScreen([st.fix.lng, st.fix.lat]);
    hud.setRecenterVisible(strayed);
  }
  mapView.map.on('moveend', refreshRecenter);

  /*
   * "Back to start".
   *
   * Every delivery is a there-and-back, and getting home meant searching for
   * an address the driver had just driven away from. The origin of the current
   * trip is remembered when navigation begins and offered as one tap once the
   * trip is over - which is exactly when it is wanted.
   */
  let tripOrigin: Place | null = null;

  /*
   * Swallow the head unit's hardware Back button.
   *
   * Reported from the road: "Hotel page going back restarts gps." Nothing in
   * the app listened for popstate, so Back went to the BROWSER, which left the
   * PWA and reloaded it on return - losing the GPS fix, the route and every
   * cached list. Pushing a sentinel state and re-pushing it on each pop means
   * Back is delivered to us instead: it closes whatever is open, and does
   * nothing at all when nothing is.
   *
   * Deliberately never calls history.back() itself, which would hand control
   * straight back to the browser and reintroduce the reload.
   */
  const closeTopmost = (): boolean => {
    // Most-recently-opened first, so Back peels one layer at a time.
    if (quickSettings.isOpen()) {
      quickSettings.close();
      return true;
    }
    if (hotelSheet.isOpen()) {
      hotelSheet.close();
      return true;
    }
    if (routeInfo.isOpen()) {
      routeInfo.close();
      return true;
    }
    // A full-screen panel sits above the route preview. Close that layer first
    // so Back from Add stop returns to the route instead of cancelling the
    // entire journey while leaving the search panel on screen.
    if (store.get().panel) {
      returnToSearchResults = false;
      if (store.get().mode === 'preview') searchTarget = 'destination';
      setPanel(null);
      return true;
    }
    if (store.get().mode === 'preview') {
      cancelPreview();
      return true;
    }
    return false;
  };

  history.pushState({ wayfarer: true }, '');
  window.addEventListener('popstate', () => {
    // Re-arm immediately: without this a second Back finds an empty stack and
    // the browser leaves the app anyway.
    history.pushState({ wayfarer: true }, '');
    closeTopmost();
  });

  const backButton = document.createElement('button');
  backButton.className = 'back-start';
  backButton.hidden = true;
  uiEl.appendChild(backButton);
  backButton.addEventListener('click', () => {
    if (tripOrigin) void pickDestination(tripOrigin);
  });

  /** Minimal escaping for the one place main.ts writes user text into HTML. */
  function escapeText(v: string): string {
    return v.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function refreshBackButton(): void {
    const st = store.get();
    // Only in idle: mid-navigation it would compete with the instruction, and
    // in preview the driver is already choosing a destination.
    const show = !!tripOrigin && st.mode === 'idle';
    backButton.hidden = !show;
    if (!show) return;
    backButton.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M11 5 L4 12 L11 19 M4 12 H15 a5 5 0 0 1 5 5 V19" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="back-start__text">
        <strong>${t('nav.backToStart')}</strong>
        <em>${escapeText(tripOrigin!.name)}</em>
      </span>`;
  }

  const vehiclePicker = new VehiclePicker(uiEl, (id) => {
    if (id === store.get().settings.activeVehicleId) return;
    store.patchSettings({ activeVehicleId: id });
    // Dimensions feed straight into the routing request, so the routes on
    // screen are wrong the moment the vehicle changes.
    if (store.get().mode === 'preview') void refetchPreviewRoutes();
    else renderPreview();
  });

  /* --------------------------- fleet + messaging ------------------------ */

  const fleet = new Fleet({
    onDrivers: (contacts) => {
      fleetBubble.update(contacts);
      mapView.setFleetDrivers(contacts);
    },
    onIncoming: (msg) => {
      fleetBubble.showIncoming(msg);
      // Spoken in THIS driver's language - the wire only carried an enum.
      announce(
        msg.kind === 'dest'
          ? t('msg.dest.voice', { name: msg.fromName })
          : t('msg.incoming.voice', { name: msg.fromName, what: t(`msg.${msg.kind}`) }),
      );
    },
    onReply: (msg, announceIt) => onMessageReply(msg, announceIt),
  });

  const fleetBubble = new FleetBubble(uiEl, {
    onSend: (to, kind) => void sendQuickMessage(to, kind),
    onReply: (id, reply) => void fleet.reply(id, reply).catch(() => {}),
    // The destination being previewed or driven to right now, ready to send.
    shareableDest: () => {
      const d = store.get().destination;
      return d ? { lng: d.center[0], lat: d.center[1], name: d.name } : null;
    },
    shareableLocation: () => {
      const fix = store.get().fix;
      return fix ? { lng: fix.lng, lat: fix.lat, name: t('fleet.currentLocation') } : null;
    },
    onShareDest: (to) => void sendDestination(to),
    onShareLocation: (to) => void sendCurrentLocation(to),
    // Accepting a shared destination mid-drive replaces the current trip -
    // that is what "go where they are going" means; the preview still shows
    // the routes first, so nothing is committed by the tap itself.
    onGotoDest: (dest) => {
      if (store.get().mode === 'navigating') endNavigation('ended');
      void pickDestination({
        id: `shared-${dest.lng},${dest.lat}`,
        name: dest.name || t('msg.dest'),
        address: '',
        center: [dest.lng, dest.lat],
        kind: 'shared',
      });
    },
  });
  openFleetDriver = (id) => fleetBubble.openDriver(id);
  fleetBubble.setUnits(store.get().settings.units);
  fleet.start();

  async function sendQuickMessage(to: number, kind: MessageKind): Promise<void> {
    const name = fleet.roster.find((c) => c.id === to)?.name ?? '';
    try {
      await fleet.send(to, kind);
      statusFlash(t('msg.sent', { name }));
      if (store.get().mode === 'navigating') hud.toast(t('msg.sent', { name }));
    } catch {
      statusFlash(t('msg.sendFailed'));
    }
  }

  /** One tap: my current destination, sent to another driver. */
  async function sendDestination(to: number): Promise<void> {
    const d = store.get().destination;
    if (!d) return;
    const name = fleet.roster.find((c) => c.id === to)?.name ?? '';
    try {
      await fleet.send(to, 'dest', { lng: d.center[0], lat: d.center[1], name: d.name });
      statusFlash(t('msg.sent', { name }));
      if (store.get().mode === 'navigating') hud.toast(t('msg.sent', { name }));
    } catch {
      statusFlash(t('msg.sendFailed'));
    }
  }

  /** Send the latest fix at tap time, never a coordinate cached when the sheet opened. */
  async function sendCurrentLocation(to: number): Promise<void> {
    const fix = store.get().fix;
    if (!fix) return;
    const name = fleet.roster.find((c) => c.id === to)?.name ?? '';
    try {
      await fleet.send(to, 'dest', { lng: fix.lng, lat: fix.lat, name: t('fleet.currentLocation') });
      statusFlash(t('msg.sent', { name }));
      if (store.get().mode === 'navigating') hud.toast(t('msg.sent', { name }));
    } catch {
      statusFlash(t('msg.sendFailed'));
    }
  }

  /**
   * `announceIt` is false for answers adopted from the backlog on a cold start:
   * they still populate the roster, but an hour-old "Yes" must not be spoken or
   * flashed as though it had just come in.
   */
  function onMessageReply(msg: QuickMessage, announceIt = true): void {
    // Backlog adopted on a cold start: fill the roster, stay silent. An answer
    // from an hour ago must not put a card on screen demanding acknowledgement.
    if (!announceIt) {
      fleetBubble.noteReply(msg);
      return;
    }

    // The card holds until the driver taps OK - no toast to miss.
    fleetBubble.showReply(msg);
    const answer = t(msg.reply === 'yes' ? 'msg.yes' : 'msg.no');
    // The reply is about a question WE asked, so `fromName` is us; use the
    // roster to name the person who actually answered.
    const who = fleet.roster.find((c) => c.id === msg.to)?.name ?? '';
    announce(t('msg.reply.voice', { name: who, answer }), 'action');
  }

  /* ------------------------------ PTT stops ----------------------------- */

  const fuelBubble = new FuelBubble(uiEl, (stop) => void takeFuelDetour(stop));
  select(st => st.mode, mode => {
    if (mode === 'navigating') {
      if (fuelBubble.container.hidden) fuelBubble.update([], null, [], store.get().settings.units);
    } else fuelBubble.hide();
  });

  /* ------------------------------ car dock ------------------------------ */
  /*
   * /car is the head-unit view: map on top with the sign on its left 45%,
   * one bar along the bottom holding media + clock + speed, the arrival bar
   * while navigating and the Where-to actions when idle. carMode itself is
   * decided up in the Spotify section, which needs it first.
   */
  const carDock = new CarDock(uiEl, {
    fuel: fuelBubble.container,
    sign: maneuverCard.el,
    fleet: fleetBubble.el,


    hud: hud.el,
    idle: idleBar,
  });

  /* --------------------------- sign size ------------------------------- */
  /*
   * "the arrows popup takes up a lot of space" on low-resolution phones.
   * Manual Compact/Normal/Large from Settings; Auto compacts on narrow
   * screens and otherwise keeps stock behavior. /car keeps its established
   * width, but gets the separate top-bar height control below.
   */
  const narrowMQ = window.matchMedia('(max-width: 380px)');
  const applySignSize = (): void => {
    const s = store.get().settings;
    const size = s.signSize;
    appEl.classList.toggle(
      'sign-compact',
      !dashboardMode && (size === 'compact' || (size === 'auto' && narrowMQ.matches)),
    );
    appEl.classList.toggle('sign-normal', !dashboardMode && size === 'normal');
    appEl.classList.toggle('sign-large', !dashboardMode && size === 'large');
    // Fine multiplier from the slider, on top of whichever preset is active.
    // At more than 100%, narrow the layout box before painting it larger so a
    // 125% sign still retains both side outlines on a 390px phone.
    const signScale = Math.min(1.25, Math.max(0.4, (s.signScale ?? 100) / 100));
    const portraitPhone = window.matchMedia('(orientation: portrait) and (max-width: 600px)').matches;
    const availableWidth = Math.max(
      160,
      document.documentElement.clientWidth - (portraitPhone ? 28 : 24),
    );
    const baseWidth = Math.min(520, availableWidth);
    const layoutWidth = Math.min(baseWidth, availableWidth / signScale);
    appEl.classList.toggle(
      'sign-stacked-followup',
      !dashboardMode && (signScale < 0.7 || signScale > 1),
    );
    // Keep the painted minimums active across the whole slider. Compact has a
    // few naturally smaller parts, so dropping the floor near 100% made those
    // parts visibly shrink as the scale increased.
    appEl.classList.toggle('sign-readable-floor', !dashboardMode);
    appEl.style.setProperty('--sign-scale', String(signScale));
    appEl.style.setProperty('--sign-layout-width', `${layoutWidth.toFixed(2)}px`);
    const readableFloor = (paintedPx: number): string => `${(paintedPx / signScale).toFixed(2)}px`;
    appEl.style.setProperty('--sign-floor-arrow', readableFloor(32));
    appEl.style.setProperty('--sign-floor-primary', readableFloor(15));
    appEl.style.setProperty('--sign-floor-secondary', readableFloor(13));
    appEl.style.setProperty('--sign-floor-road-label', readableFloor(12));
    appEl.style.setProperty('--sign-floor-road-name', readableFloor(14));
    appEl.style.setProperty('--sign-floor-then', readableFloor(12));
    appEl.style.setProperty('--sign-floor-then-width', readableFloor(40));
    appEl.style.setProperty('--sign-floor-then-glyph', readableFloor(28));
    appEl.style.setProperty('--sign-floor-then-distance', readableFloor(13));
    appEl.style.setProperty('--sign-floor-lane-width', readableFloor(36));
    appEl.style.setProperty('--sign-floor-lane-height', readableFloor(42));
    appEl.style.setProperty('--sign-floor-lane-glyph-width', readableFloor(26));
    appEl.style.setProperty('--sign-floor-lane-glyph-height', readableFloor(34));

    // Height changes the top navigation bar without squeezing or stretching
    // its text. A small value reduces padding; content remains the hard floor.
    // A large value grows a safe minimum that the measured rail then clears.
    const barScale = Math.min(1.25, Math.max(0.6, (s.signBarHeight ?? 100) / 100));
    // On the shortest supported dashboard, 110px is the tallest bar that
    // still leaves a full vehicle-marker corridor above the lane arrows.
    const barMinHeight = Math.min(110, 90 * barScale);
    appEl.style.setProperty('--sign-bar-min-height', `${barMinHeight.toFixed(1)}px`);
    appEl.style.setProperty('--sign-bar-padding-y', `${(14 * barScale).toFixed(1)}px`);
    appEl.style.setProperty('--sign-bar-padding-y-short', `${(7 * barScale).toFixed(1)}px`);
    const laneGuideScale = Math.min(1.5, Math.max(0.9, (s.laneGuideScale ?? 100) / 100));
    appEl.style.setProperty('--lane-guide-scale', String(laneGuideScale));
    // A transform changes the card's VISUAL height without firing its
    // ResizeObserver - re-measure so the right-rail buttons stay clear.
    measureRail();
  };
  if (typeof narrowMQ.addEventListener === 'function') {
    narrowMQ.addEventListener('change', applySignSize);
  } else {
    (narrowMQ as MediaQueryList).addListener(applySignSize);
  }
  select((s) => s.settings.signSize, applySignSize);
  select((s) => s.settings.signScale, applySignSize, { immediate: false });
  select((s) => s.settings.signBarHeight, applySignSize, { immediate: false });
  select((s) => s.settings.laneGuideScale, applySignSize, { immediate: false });
  window.addEventListener('resize', applySignSize);

  // Move existing DOM on resize: no reload, map recreation, or route interruption.
  const refreshDock = (): void => {
    const changed = dashboardMode !== dashboardMQ.matches;
    dashboardMode = dashboardMQ.matches;
    appEl.classList.toggle('app--car', dashboardMode);
    carDock.setLayout(dashboardMode ? 'bottom' : null);
    if (changed) {
      maneuverCard.setDetachedThen(dashboardMode);
      const state = store.get();
      if (state.progress) maneuverCard.update(state.progress, state.settings.units, state.fix?.speed ?? 0);
      applySignSize();
      mapView.setCameraLeftInset(0);
      mapView.setCameraBottomInset(dashboardMode ? carDock.el.offsetHeight : 0);
      mapView.setCameraTopInset(0);
    }
  };
  if (typeof dashboardMQ.addEventListener === 'function') dashboardMQ.addEventListener('change', refreshDock);
  else dashboardMQ.addListener(refreshDock);
  select((s) => s.mode, refreshDock, { immediate: false });
  refreshDock();

  /* Live telemetry - everything being recorded, as it happens. */
  let liveSamples = 0;
  let liveRough: number | null = null;
  const livePanel = new LiveDataPanel(uiEl, () => {
    const st = store.get();
    const prog = st.progress;
    return {
      fix: st.fix,
      progress: st.mode === 'navigating' ? prog : null,
      roughness: liveRough,
      tier: prog ? tierForStep(prog.step) : null,
      tripDistance: recorder?.distance ?? 0,
      samples: liveSamples,
      movingSeconds: Math.round(liveMovingSeconds),
      gradeNow: gradeAtCurrentPosition(),
      signalsOnRoute: routeStats[st.selectedRouteIndex]?.trafficSignals ?? null,
      fleetCount: fleet.roster.length,
      units: st.settings.units,
    };
  });
  let liveMovingSeconds = 0;
  let lastFixAt = 0;

  /** Gradient under the wheels right now, from the loaded elevation profile. */
  function gradeAtCurrentPosition(): number | null {
    const prog = store.get().progress;
    if (!elevation || !prog) return null;
    const at = prog.snapped.distanceAlongRoute;
    const s1 = elevation.samples;
    let lo = 0;
    for (let i = 1; i < s1.length; i++) {
      if (s1[i][0] > at) break;
      lo = i;
    }
    const a = s1[lo];
    const b = s1[Math.min(s1.length - 1, lo + 2)];
    const run = b[0] - a[0];
    if (run <= 0) return null;
    return Math.round(((b[1] - a[1]) / run) * 1000) / 10;
  }

  /* Everything worth knowing about the journey, in one place. */
  const routeInfo = new RouteInfoSheet(uiEl, () => {
    const st = store.get();
    return {
      route: st.routes[st.selectedRouteIndex] ?? navigator_?.route ?? null,
      stats: routeStats[st.selectedRouteIndex] ?? routeStats[0] ?? null,
      elevation,
      progress: st.mode === 'navigating' ? st.progress : null,
      fuelStop: fuelStops[0] ?? null,
      units: st.settings.units,
      speeds: st.settings.speeds,
      vehicleName: activeVehicle(st.settings).name,
    };
  });
  /*
   * On-demand route second opinion. The traffic watcher offers a switch on its
   * own timer; this is for the driver who can see the queue and wants an answer
   * now. Shares switchToRoute() with the watcher so an accepted answer is
   * applied by exactly the same path.
   */
  const askAi = new AskAiButton(
    uiEl,
    (question) => void askAboutRoute(question),
    (route) => switchToRoute(route, askRouteStops),
  );
  /** Stops used to calculate the choices currently shown by Ask AI. */
  let askRouteStops: Place[] = [];

  /*
   * Hotels. Fetched on demand when the sheet is opened, never on a timer: a
   * search is several category calls per probe point and the answer does not
   * change minute to minute, so polling would spend the car's connection on
   * nothing. Results are cached until the destination or route changes.
   */
  let hotelsRoute: Hotel[] | null = null;
  let hotelsDest: Hotel[] | null = null;
  let hotelsHere: Hotel[] | null = null;
  let hotelError: string | null = null;
  let hotelAbort: AbortController | null = null;

  const hotelSheet = new HotelSheet(
    uiEl,
    () => {
      const st = store.get();
      return {
        route: hotelsRoute,
        destination: hotelsDest,
        here: hotelsHere,
        hasFix: !!st.fix,
        units: st.settings.units,
        destinationName: st.destination?.name ?? null,
        navigating: st.mode === 'navigating',
        error: hotelError,
      };
    },
    {
      onNeed: (tab) => void loadHotels(tab),
      onNavigate: (hotel) => {
        void pickDestination({
          id: `hotel:${hotel.id}`,
          name: hotel.name,
          address: hotel.address,
          center: hotel.center,
          kind: 'poi',
        });
      },
    },
  );

  {
    const tripsButton = idleBar.querySelector<HTMLButtonElement>('[data-act=trips]')!;
    const shareButton = idleBar.querySelector<HTMLButtonElement>('[data-act=share]')!;
    const settingsButton = idleBar.querySelector<HTMLButtonElement>('[data-act=settings]')!;
    const whileNavigating = () => store.get().mode === 'navigating';
    quickSettings.setActions([
      {
        id: 'trips',
        label: t('trips.title'),
        icon: tripsButton.innerHTML,
        run: () => tripsButton.click(),
      },
      {
        id: 'share',
        label: t('share.title'),
        icon: shareButton.innerHTML,
        run: () => shareButton.click(),
      },
      {
        id: 'settings',
        label: t('set.title'),
        icon: settingsButton.innerHTML,
        run: () => settingsButton.click(),
      },
      {
        id: 'route',
        label: t('info.title'),
        icon: routeInfo.button.innerHTML,
        run: () => routeInfo.open(),
        visible: whileNavigating,
      },
      {
        id: 'live',
        label: t('live.title'),
        icon: livePanel.button.innerHTML,
        run: () => livePanel.open(),
        visible: whileNavigating,
      },
      {
        id: 'ask',
        label: t('ask.title'),
        icon: askAi.button.innerHTML,
        run: () => askAi.open(),
        visible: whileNavigating,
      },
      {
        id: 'hotels',
        label: t('hotel.title'),
        icon: hotelSheet.button.innerHTML,
        run: () => hotelSheet.open(),
        visible: whileNavigating,
      },
    ]);

    const compactPhone = (): boolean =>
      window.matchMedia('(max-width: 900px), (orientation: landscape) and (max-height: 620px)').matches;
    const syncPhoneTools = (): void => {
      const mode = store.get().mode;
      quickSettings.setVisible(mode === 'navigating' || (compactPhone() && mode === 'idle'));
    };
    window.addEventListener('resize', syncPhoneTools);
    syncPhoneTools();
  }

  /** Fetch one tab's list, unless it is already in hand. */
  async function loadHotels(tab: HotelTab): Promise<void> {
    const st = store.get();
    const have =
      tab === 'route' ? hotelsRoute : tab === 'here' ? hotelsHere : hotelsDest;
    if (have !== null) return;

    hotelError = null;
    hotelAbort?.abort();
    hotelAbort = new AbortController();
    const signal = hotelAbort.signal;

    try {
      if (tab === 'route') {
        const route = navigator_?.route ?? activeRoute();
        if (!route) {
          hotelError = t('hotel.noneRoute');
        } else {
          hotelsRoute = await findHotelsAhead({
            route,
            alongNow: st.progress?.snapped.distanceAlongRoute ?? 0,
            signal,
          });
        }
      } else if (tab === 'here') {
        /*
         * Around the CURRENT position, not the route or the destination - the
         * driver who has decided to stop now and wants what is within reach.
         * An empty array rather than an error with no fix, so the sheet can say
         * "waiting for GPS" instead of "none found", which would be a lie.
         */
        const fix = st.fix;
        // 20, not 8: within a 5 km radius a Thai town holds 20-30 hotels, and a
        // list capped at eight threw most of them away - "still only 8 hotels
        // around here". The sheet scrolls, so the cap only needs to stop an
        // absurd list, not to curate one.
        hotelsHere = fix ? await findHotelsNear([fix.lng, fix.lat], 20, signal) : [];
      } else {
        const dest = st.destination;
        if (!dest) {
          hotelError = t('hotel.noneDest');
        } else {
          hotelsDest = await findHotelsNear(dest.center, 20, signal);
        }
      }
    } catch {
      // Advisory feature - a failed search must never interrupt a drive.
      hotelError = t('hotel.noneDest');
    }
    hotelSheet.refresh();
  }

  /** Both lists are about a particular journey; a new one invalidates them. */
  function clearHotelCache(): void {
    hotelsRoute = null;
    hotelsDest = null;
    // "Around here" is about where the vehicle is, which changes as it drives -
    // but re-searching on every fix would be absurd, so it is cleared with the
    // rest and refreshed the next time the sheet is opened for a new journey.
    hotelsHere = null;
    hotelError = null;
    hotelAbort?.abort();
    hotelAbort = null;
  }

  let fuelStops: FuelStop[] = [];
  // Other brands belong in the separate chooser; the pill requires PTT + 7-Eleven.
  let allFuel: FuelStop[] = [];
  let fuelSearchedAt = -Infinity;
  let fuelSearchInFlight = false;
  let fuelLastRequestAt = 0;
  /** The route we were on before a fuel detour, so Undo can put it back. */
  let routeBeforeDetour: Route | null = null;
  type RouteWaypointKind = 'user' | 'temporary';
  /** Intermediate legs on the route currently owned by the navigator. */
  let routeWaypointKinds: RouteWaypointKind[] = [];
  /** The route that produced the last NavProgress leg index. */
  let stopProgressRoute: Route | null = null;
  let routeBeforeDetourStops: Place[] = [];
  let routeBeforeDetourWaypointKinds: RouteWaypointKind[] = [];

  /**
   * Live refinement: ask about THIS junction when we are close, the rules have
   * produced nothing, and the prefetch did not cover it.
   *
   * The prefetch handles the ordinary case. This exists for the ones it cannot:
   * a reroute or fuel detour built a step list the prefetch never saw, or the
   * junction was there but the model declined it the first time and we are now
   * close enough that silence is expensive. Guarded three ways - only when
   * blank, only inside REFINE_AT_M, and deduped per index inside the cache - so
   * a junction costs at most one call however many fixes arrive on the approach.
   */
  const REFINE_AT_M = 1_200;
  function refineGuideIfBlank(progress: NavProgress): void {
    if (!laneGuides || !navigator_) return;
    const fork = progress.fork;
    if (progress.aiGuide) return;

    const distance = fork ? fork.distance : progress.distanceToManeuver;
    if (distance > REFINE_AT_M) return;

    const steps = navigator_.route.legs.flatMap((l) => l.steps);
    const target = progress.maneuverStep;
    const idx = fork ? fork.stepIndex : target ? steps.indexOf(target) : -1;
    if (idx < 1 || idx >= steps.length) return;

    const step = steps[idx];
    const prev = steps[idx - 1] ?? null;
    const rawLanes = resolveLanes(fork ? null : progress.banner, step);
    const modifier = step.maneuver.modifier;
    const measuredUturn =
      (modifier ?? '').toLowerCase() === 'uturn'
        ? uturnSide(prev, step, progress.drivingSide)
        : null;
    const deterministic = laneAdvice(
      rawLanes,
      progress.drivingSide,
      modifier,
      measuredUturn,
    );
    // Constrained lanes are actionable deterministic guidance and stay in
    // charge. An all-valid lane array is just arrows, so AI may still explain
    // the junction. Contradictory lane data is different: the rules rejected
    // it for safety, and AI must not turn it into an instruction.
    if (rawLanes?.length && !deterministic) return;
    if (deterministic?.changeRequired) return;

    void laneGuides.refine(
      idx,
      prev,
      step,
      getLang(),
      progress.drivingSide,
    );
  }

  async function refreshFuelStop(along: number): Promise<void> {
    const route = navigator_?.route;
    if (!route || fuelSearchInFlight) return;
    if (fuelSearchedAt !== -Infinity && Date.now() - fuelLastRequestAt < 60_000) return;
    // Re-query only every 5 km, or once the chosen stop is behind us.
    const stale = fuelStops[0] ? along > fuelStops[0].along - 300 : false;
    if (!stale && Math.abs(along - fuelSearchedAt) < 5000) return;

    fuelSearchInFlight = true;
    fuelLastRequestAt = Date.now();
    fuelSearchedAt = along;
    try {
      const stops = await findFuelStopsAhead({
        route,
        along,
        drivingSide: store.get().progress?.drivingSide ?? 'left',
        signal: AbortSignal.timeout(8_000),
      });
      // Keep a couple: the sheet offers a second choice when the nearest is
      // served by a frontage road.
      if (navigator_?.route !== route) return;
      allFuel = stops.all;
      const qualified = await pttWithSevenEleven(stops.ptt, route);
      if (navigator_?.route !== route) return;
      fuelStops = qualified.slice(0, 2);

    } catch {
      /* leave the previous stop in place rather than flickering the bubble */
    } finally {
      fuelSearchInFlight = false;
    }
  }

  async function takeFuelDetour(stop: FuelStop): Promise<void> {
    const dest = store.get().destination;
    const fix = store.get().fix;
    if (!dest || !fix || !navigator_) return;
    const basedOnRoute = navigator_.route;
    const remainingStops = remainingRouteStops();
    const waypointEntries = [
      ...remainingStops.map((place) => ({
        kind: 'user' as const,
        place,
        point: place.center,
        along: projectPointOntoLine(place.center, basedOnRoute.geometry.coordinates)?.along ?? Infinity,
      })),
      {
        kind: 'temporary' as const,
        place: null,
        point: stop.location,
        along: stop.along,
      },
    ].sort((a, b) => a.along - b.along);

    hud.toast(t('fuel.detour', { name: stop.name }), true);
    try {
      const routes = await fetchRoutes({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        // Routing THROUGH the station means the trip carries on to the real
        // destination by itself once we pull out - nothing to unwind.
        via: waypointEntries.map((entry) => entry.point),
        heading: fix.heading,
        settings: { ...store.get().settings, route: { ...store.get().settings.route, alternatives: false } },
      });
      if (!navigator_ || navigator_.route !== basedOnRoute || store.get().mode !== 'navigating') return;
      const s2 = store.get().settings;
      const detour = applyPace(routes, s2.speeds, s2.signalSeconds)[0];
      if (!detour || !(await truckRouteAllowed(detour)) || navigator_?.route !== basedOnRoute || store.get().mode !== 'navigating') return;
      routeBeforeDetour = navigator_.route;
      routeBeforeDetourStops = routeStops.map((place) => ({
        ...place,
        center: [...place.center] as LngLat,
      }));
      routeBeforeDetourWaypointKinds = [...routeWaypointKinds];
      setNavigatingStops(
        waypointEntries
          .map((entry) => entry.place)
          .filter((place): place is Place => place !== null),
        waypointEntries.map((entry) => entry.kind),
      );
      if (!detour) throw new Error('No fuel detour found');
      navigator_.replaceRoute(detour);
      resetLaneGuides(detour);
      routeRough = roughnessOfRoute(detour, roughIndex, store.get().settings.roughSensitivity);
      routeCumulative = cumulativeDistances(detour.geometry.coordinates);
      routeStats = [computeRouteStats(detour)];
      store.set({ routes: [detour], selectedRouteIndex: 0 });
      mapView.drawRoute(detour);
      void scanManeuverStructures([detour]).catch(() => {});
      fuelStops = [];
      allFuel = [];
      fuelBubble.update([], null, [], s2.units);
      hud.toast(t('fuel.detour', { name: stop.name }), false, {
        label: t('fuel.undo'),
        onAction: () => undoFuelDetour(),
      });
    } catch {
      hud.toast(t('fuel.failed'));
    }
  }

  function undoFuelDetour(): void {
    const previous = routeBeforeDetour;
    if (!previous || !navigator_) return;
    routeBeforeDetour = null;
    setNavigatingStops(routeBeforeDetourStops, routeBeforeDetourWaypointKinds);
    routeBeforeDetourStops = [];
    routeBeforeDetourWaypointKinds = [];
    navigator_.replaceRoute(previous);
    resetLaneGuides(previous);
    routeCumulative = cumulativeDistances(previous.geometry.coordinates);
    routeStats = [computeRouteStats(previous)];
    routeRough = roughnessOfRoute(previous, roughIndex, store.get().settings.roughSensitivity);
    store.set({ routes: [previous], selectedRouteIndex: 0 });
    mapView.drawRoute(previous);
    fuelSearchedAt = -Infinity;
    hud.toast(t('fuel.restored'));
  }

  idleBar.querySelector('[data-act=search]')!.addEventListener('click', () => {
    primeSpeech(); // user gesture unlocks TTS for later
    returnToSearchResults = false;
    searchTarget = 'destination';
    setPanel('search');
  });
  idleBar.querySelector('[data-act=settings]')!.addEventListener('click', () => setPanel('settings'));
  idleBar.querySelector('[data-act=trips]')!.addEventListener('click', () => setPanel('trips'));
  idleBar.querySelector('[data-act=share]')!.addEventListener('click', () => {
    const f = store.get().fix;
    if (!f) {
      // Indoors / basement / no signal: instead of dead-ending on "try again",
      // arm pin-share - the next long-press on the map saves THAT spot.
      pinShareArmed = true;
      statusFlash(t('idle.noFixPin'));
      return;
    }
    // A fix is available, so this share uses it - drop any stale armed state
    // from an earlier no-fix press, or the next long-press would hijack it.
    pinShareArmed = false;
    // Name it NOW, in one popup, rather than editing the auto-label later -
    // "ปั๊มยาง Williams" is findable next month; "Petteri · 14:32" is not.
    // The position is captured before the dialog, not after: the vehicle may
    // move while the name is being typed, and the place being saved is where
    // the button was pressed.
    const at: LngLat = [f.lng, f.lat];
    const now = new Date();
    const label = `${currentUser?.name ?? 'Driver'} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    void openNameDialog(t('share.title'), label).then((name) => {
      if (name == null) return;
      void authApi
        .createPlace(name, at[0], at[1], 'shared-location')
        .then(async () => {
          await loadPlaces();
          statusFlash(t('idle.shared'));
        })
        .catch(() => statusFlash(t('idle.shareFailed')));
    });
  });

  let statusFlashTimer: number | null = null;
  function statusFlash(message: string): void {
    statusEl.textContent = message;
    statusEl.hidden = false;
    if (statusFlashTimer !== null) clearTimeout(statusFlashTimer);
    statusFlashTimer = window.setTimeout(() => {
      statusEl.hidden = true;
    }, 3500);
  }

  function setPanel(
    panel: 'search' | 'settings' | 'trips' | null,
    preserveSearchResults = false,
  ): void {
    searchPanel.close(preserveSearchResults);
    settingsPanel.close();
    tripsPanel.close();
    if (panel === 'search') {
      searchPanel.setPlaceholder(
        searchTarget === 'stop'
          ? t('search.stopPh')
          : searchTarget === 'origin'
            ? t('search.originPh')
            : t('search.ph'),
      );
      searchPanel.open(preserveSearchResults);
    }
    if (panel === 'settings') void settingsPanel.open();
    if (panel === 'trips') void tripsPanel.open();
    store.set({ panel });
  }

  // A share target imports human-readable text only. It opens an ordinary
  // Mapbox search and stops there; selecting a result and starting its preview
  // remain explicit driver actions.
  const sharedPlaceImport = readSharedPlaceImport();
  if (sharedPlaceImport) {
    clearSharedPlaceImportUrl();
    returnToSearchResults = false;
    searchTarget = 'destination';
    setPanel('search');
    searchPanel.openSharedImport(sharedPlaceImport.query);
  }

  /* -------------------------------- GPS -------------------------------- */
  const statusEl = idleBar.querySelector('.idlebar__status') as HTMLElement;
  let tripResumeInFlight = false;

  /*
   * Android may either freeze this document or kill and recreate the PWA when
   * another app takes the foreground. Checkpoint before either outcome, and
   * restore on both the same-document resume and a pageshow from back/forward
   * cache. A full process restart is covered by the first GPS fix below.
   */
  const checkpointActiveTrip = (): void => {
    const s = store.get();
    if (carMode && s.mode === 'navigating' && s.destination) {
      rememberActiveTrip(s.destination, remainingRouteStops());
    }
  };
  window.addEventListener('pagehide', checkpointActiveTrip);
  window.addEventListener('wayfarer:checkpoint', checkpointActiveTrip);
  if (carMode) installDriveRecovery(uiEl);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) checkpointActiveTrip();
    else void resumeActiveTripIfNeeded();
  });
  window.addEventListener('pageshow', () => void resumeActiveTripIfNeeded());

  // The push worker renders notifications in this device's language and cannot
  // read localStorage, so keep its copy current from the moment we boot.
  void mirrorLangForWorker();

  trace('ui:ok');
  const gps = new GpsWatcher({
    onFix: (fix) => {
      const first = !store.get().located;
      store.set({ fix, located: true, gpsError: null });
      hud.updateSpeed(fix.speed, store.get().settings.units);
      statusEl.hidden = true;
      if (first) mapView.jumpTo([fix.lng, fix.lat], 15.5);

      if (first) void resumeActiveTripIfNeeded();

      fleet.setFix(fix);
      if (breadcrumbs.add(fix)) redrawCrumbs();
      refreshRecenter();
      const mode = store.get().mode;
      if (mode === 'navigating') {
        // A dev-only structure fixture owns the maneuver card; live simulated
        // fixes would otherwise replace it before responsive QA can inspect it.
        if (!devVisualFixture) onNavFix(fix);
      } else if (mode === 'idle') {
        // Driving with no destination set still needs the map to follow, or
        // the arrow walks off the screen. 'searching' and 'preview' are left
        // alone deliberately - see followIdle().
        mapView.followIdle(fix);
      } else {
        mapView.updatePuck(fix);
      }
    },
    onError: (message) => {
      store.set({ gpsError: message });
      statusEl.textContent = message;
      statusEl.hidden = false;
    },
  });
  if (!devVisualFixture) gps.start();

  function currentFixLngLat(): LngLat | null {
    const f = store.get().fix;
    return f ? [f.lng, f.lat] : null;
  }

  /* ------------------------------ routing ------------------------------ */
  let routeAbort: AbortController | null = null;
  /** Traffic-light / turn / toll counts for the current candidate routes. */
  let routeStats: RouteStats[] = [];
  let advice: RouteAdvice | null = null;
  /** Gradient profile of the selected route for the active vehicle. */
  let elevation: ElevationProfile | null = null;
  let adviceLoading = false;
  let adviceDismissed = false;
  let lastAdviceKey = '';
  let adviceAbort: AbortController | null = null;

  /**
   * Armed by the share button when there is no GPS fix: the next long-press
   * saves that spot for the fleet instead of opening a route preview, so a
   * place can still be shared from a basement or before the first fix.
   */
  let pinShareArmed = false;

  async function pickDroppedPin(point: LngLat): Promise<void> {
    if (store.get().mode === 'navigating') return;
    if (pinShareArmed) {
      pinShareArmed = false;
      const now = new Date();
      const label = `${currentUser?.name ?? 'Driver'} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const name = await openNameDialog(t('share.title'), label);
      if (name == null) return;
      try {
        await authApi.createPlace(name, point[0], point[1], 'shared-location');
        await loadPlaces();
        statusFlash(t('idle.shared'));
      } catch {
        statusFlash(t('idle.shareFailed'));
      }
      return;
    }
    const place = await reverseGeocode(point);
    void pickDestination(place);
  }

  const ROUTE_SYNC_POLL_MS = 2_000;
  const routeSyncDevice = (() => {
    const key = 'wayfarer.routeDevice';
    try {
      const existing = localStorage.getItem(key);
      if (existing) return existing;
      const id =
        typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, id);
      return id;
    } catch {
      return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
  })();
  let currentRoutePlanId: string | null = null;
  let lastSyncedRouteAt = 0;
  let routeSyncBusy = false;
  let pendingSyncedRoute: authApi.UserRoute | null = null;

  function nextRoutePlanId(): string {
    return `route-${Date.now().toString(36)}-${routeSyncDevice.slice(-10)}`;
  }

  /** Store a route under the authenticated user; failures never block routing. */
  function publishUserRoute(
    place: Place,
    status: authApi.UserRouteStatus,
    id = currentRoutePlanId ?? nextRoutePlanId(),
  ): void {
    currentRoutePlanId = id;
    void authApi
      .saveUserRoute({ id, destination: place, stops: routeStops, status, sourceDevice: routeSyncDevice })
      .then(({ route }) => {
        if (currentRoutePlanId === route.id) lastSyncedRouteAt = route.updatedAt;
      })
      .catch(() => {});
  }

  async function pickDestination(
    place: Place,
    publish = true,
    fromSearchResults = false,
    stops: Place[] = [],
  ): Promise<void> {
    returnToSearchResults = fromSearchResults;
    routeStops = stops.slice(0, 8).map((stop) => ({ ...stop, center: [...stop.center] as LngLat }));
    if (publish) {
      currentRoutePlanId = nextRoutePlanId();
      // Publish before the Directions call: the head unit sees the destination
      // immediately even when route calculation on the phone takes a while.
      publishUserRoute(place, 'planned', currentRoutePlanId);
    }
    setPanel(null, fromSearchResults);
    backButton.hidden = true;
    advice = null;
    adviceDismissed = false;
    lastAdviceKey = '';
    store.set({ destination: place, mode: 'preview', routes: [], routeError: null });
    // Leg picks belong to the journey that was being planned; carrying them
    // into a new destination would route it through corridors chosen for a
    // different trip entirely.
    legPicks = [];
    legVias = [];
    clearHotelCache();
    hotelSheet.setVisible(true);
    mapView.setDestination(place.center);
    mapView.setStops(routeStops.map((stop) => stop.center));
    void addRecent(place);
    await refetchPreviewRoutes();
  }

  async function applyRouteStops(stops: Place[]): Promise<void> {
    routeStops = stops.slice(0, 8);
    searchTarget = 'destination';
    legPicks = [];
    legVias = [];
    mapView.setStops(routeStops.map((stop) => stop.center));
    clearHotelCache();
    const destination = store.get().destination;
    if (destination) publishUserRoute(destination, 'planned');
    await refetchPreviewRoutes();
  }

  async function addRouteStop(place: Place): Promise<void> {
    const destination = store.get().destination;
    if (!destination || store.get().mode !== 'preview') return;
    searchTarget = 'destination';
    setPanel(null);
    if (routeStops.length >= 8) {
      statusFlash(t('route.maxStops'));
      return;
    }
    if (
      distance(place.center, destination.center) < 50 ||
      routeStops.some((stop) => distance(stop.center, place.center) < 50)
    ) {
      statusFlash(t('route.duplicateStop'));
      renderPreview();
      return;
    }

    // Insert where it lies on the currently selected route. Adding a stop
    // found two hours ahead must not jump in front of one ten minutes away.
    const line = activeRoute()?.geometry.coordinates ?? [];
    const ordered = [...routeStops, place]
      .map((stop, index) => ({
        stop,
        index,
        along:
          stop === place && Number.isFinite(place.routeAlong)
            ? (place.routeAlong as number)
            : projectPointOntoLine(stop.center, line)?.along ?? Infinity,
      }))
      .sort((a, b) => a.along - b.along || a.index - b.index)
      .map((entry) => entry.stop);
    void addRecent(place);
    await applyRouteStops(ordered);
  }

  async function removeRouteStop(index: number): Promise<void> {
    if (index < 0 || index >= routeStops.length) return;
    await applyRouteStops(routeStops.filter((_, i) => i !== index));
  }

  async function moveRouteStop(index: number, delta: -1 | 1): Promise<void> {
    const to = index + delta;
    if (index < 0 || index >= routeStops.length || to < 0 || to >= routeStops.length) return;
    const next = [...routeStops];
    [next[index], next[to]] = [next[to], next[index]];
    await applyRouteStops(next);
  }

  /** Rebuild an ignition-interrupted drive from the car's current GPS fix. */
  async function resumeActiveTripIfNeeded(): Promise<void> {
    if (!carMode || tripResumeInFlight || !store.get().fix || store.get().mode === 'navigating') return;
    const interruptedTrip = loadActiveTrip();
    if (!interruptedTrip) return;
    tripResumeInFlight = true;
    statusEl.textContent = t('nav.resuming', { name: interruptedTrip.destination.name });
    statusEl.hidden = false;
    try {
      await pickDestination(interruptedTrip.destination, true, false, interruptedTrip.stops);
      if (store.get().routes.length && store.get().destination) startNavigation();
    } catch {
      // Keep the record. A later restart with connectivity can try again; the
      // ordinary preview exposes the routing error in the current session.
    } finally {
      tripResumeInFlight = false;
    }
  }

  /**
   * Which arm the driver has chosen at each decision, by choice index.
   *
   * Held rather than derived: the whole point is that the chosen combination
   * may be one no single returned route takes, so there is nothing to derive
   * it from.
   */
  let legPicks: LegPick[] = [];
  /** Via-points implied by legPicks, consumed by refetchPreviewRoutes(). */
  let legVias: LngLat[] = [];

  /**
   * Choose one arm of one decision and re-route through it.
   *
   * The chips used to select a whole route, which meant a journey with two
   * decisions only ever offered the pairings the router happened to return -
   * "if i pick b i cant pick the other route coming from the 24. its either
   * or." Routing through a point on each chosen arm lets the choices compose,
   * so every combination becomes reachable.
   */
  async function pickLeg(choiceIndex: number, routeIndex: number): Promise<void> {
    const st = store.get();
    if (!st.destination || !st.routes.length || routeStops.length) return;

    const choices = routeChoices(st.routes);
    if (!choices[choiceIndex]) return;

    // One arm per fork: a new pick at the same decision replaces the old one.
    legPicks = [
      ...legPicks.filter((p2) => p2.choiceIndex !== choiceIndex),
      { choiceIndex, routeIndex },
    ].sort((a2, b2) => a2.choiceIndex - b2.choiceIndex);

    legVias = viasForPicks(st.routes, choices, legPicks);
    await refetchPreviewRoutes();
  }

  async function refetchPreviewRoutes(): Promise<void> {
    const state = store.get();
    const dest = state.destination;
    const origin = plannedOrigin ? plannedOrigin.center : currentFixLngLat();
    routeAbort?.abort();
    const requestController = new AbortController();
    routeAbort = requestController;
    const stillCurrent = () => !requestController.signal.aborted && routeAbort === requestController && store.get().destination === dest;
    if (!dest) return;
    if (!origin) {
      /*
       * No GPS, but the trip is still plannable.
       *
       * This used to sit on "Waiting for a GPS fix…" indefinitely, which is
       * the wrong answer for a driver working out tomorrow's run from a
       * basement, a workshop or anywhere else with no sky. The destination is
       * known and perfectly good; the only missing piece is where to start
       * FROM, and the sheet already has a row for exactly that. So say so, and
       * show the place on the map meanwhile - it is the thing being decided
       * about, and leaving the map wherever it happened to be made it look as
       * though the search had failed.
       */
      mapView.jumpTo(dest.center, 14.5);
      store.set({ routeError: t('preview.noFixSetStart') });
      preview.render({
        destination: dest,
        stops: routeStops,
        routes: [],
        stats: [],
        selectedIndex: 0,
        loading: false,
        error: t('preview.noFixSetStart'),
        isSaved: isDestSaved(),
        originName: plannedOrigin?.name ?? null,
        noFix: true,
      });
      return;
    }

    store.set({ routing: true, routeError: null });
    preview.render({
      destination: dest,
      stops: routeStops,
      routes: [],
      stats: [],
      selectedIndex: 0,
      loading: true,
      error: null,
    });

    try {
      // Corridors this driver has used before for the same destination, so a
      // route they know is better than the engine's gets offered explicitly.
      const past = await listTrips(100);
      if (!stillCurrent()) return;
      // Learned corridors and hand-picked shape points describe a direct
      // origin-to-goal journey. Real stops are different: every one must stay
      // visible, ordered and announced, so never mix the two waypoint models.
      const learned = routeStops.length
        ? []
        : learnedVias(past, origin, dest.center).map((c) => c.vias);

      const fetched = await fetchRouteAlternatives(
        {
          origin,
          destination: dest.center,
          heading: plannedOrigin ? null : (state.fix?.heading ?? null),
          settings: store.get().settings,
          signal: requestController.signal,
          // Set when the driver has chosen legs by hand - see pickLeg(). Forces
          // the route through the corridors they picked, which is what makes
          // combinations the router never offered reachable at all.
          via: routeStops.length
            ? routeStops.map((stop) => stop.center)
            : legVias.length
              ? legVias
              : undefined,
        },
        learned,
      );
      if (!stillCurrent()) return;
      // Fastest first deliberately replaces the former expressway-first policy.
      // Keep roughness/width information visible without secretly changing the ETA order.
      const s = store.get().settings;
      const routes = fastestRouteOrder(applyPace(fetched, s.speeds, s.signalSeconds), activeVehicle(s).kind === 'truck');
      roughPerRoute = routes.map(r => roughnessOfRoute(r, roughIndex, s.roughSensitivity));
      widthPerRoute = routes.map(roadWidthProfile);
      for (const route of routes) if (route._tag === 'fastest') route._tag = undefined;
      if (routes.length) routes[0]._tag = 'fastest';

      routeStats = statsFor(routes);
      pttPerRoute = [];
      clearancePerRoute = [];
      store.set({ routes, selectedRouteIndex: 0, routing: false });
      routeRough = roughPerRoute[0] ?? null;
      elevation = null;
      renderPreview();
      requestAnimationFrame(fitPreview);
      routeInfo.setVisible(true);
      void refreshElevation();
      void requestAdvice();
      void refreshPttGaps(routes, requestController.signal);
      void refreshClearances(routes, requestController.signal);
      void scanManeuverStructures(routes, requestController.signal).catch(() => {});
    } catch (err) {
      if (!stillCurrent()) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Routing failed.';
      routeStats = [];
      store.set({ routing: false, routeError: message, routes: [] });
      renderPreview();
    }
  }

  /**
   * PTT spacing per option, off the critical path like the advisor: the list
   * renders immediately and the fuel rows fill in when the searches land.
   * Computed once per fetch - selecting between A/B/C re-renders from the
   * cached array and costs no further requests.
   */
  async function refreshPttGaps(routes: Route[], signal: AbortSignal): Promise<void> {
    // One route at a time - all three at once triples the request burst and
    // trips the same per-token rate limit the chunking in pttGapStats avoids.
    // Each route's row fills in as its own searches land.
    const stats: Array<PttGapStats | null> = routes.map(() => null);
    for (let i = 0; i < routes.length; i++) {
      stats[i] = await pttGapStats(routes[i], signal).catch(() => null);
      if (signal.aborted || store.get().routes !== routes) return; // stale fetch
      pttPerRoute = [...stats];
      renderPreview();
    }
  }

  /**
   * Posted height limits per option, for the truck only - a car fits under
   * everything the routing would ever offer it, and the lookup is a request
   * to a public server that should not be spent for nothing. Off the critical
   * path like the fuel rows: the sheet renders immediately and the clearance
   * lines fill in when the scan lands. A failed scan leaves the array empty,
   * which the sheet reads as "unknown" and says nothing - never "clear".
   */
  async function refreshClearances(routes: Route[], signal: AbortSignal): Promise<void> {
    const v = activeVehicle(store.get().settings);
    if (v.kind !== 'truck' || !v.height) return;
    const scanned = await scanRouteClearances(routes, v.height, signal).catch(() => null);
    if (!scanned || signal.aborted || store.get().routes !== routes) return;
    clearancePerRoute = scanned;
    renderPreview();
  }

  /**
   * Ask the advisor for a second opinion. Entirely best-effort and entirely
   * off the critical path - the driver can hit GO before it ever answers, and
   * a server with no API key configured simply never shows the card.
   */
  async function requestAdvice(): Promise<void> {
    const s = store.get();
    const dest = s.destination;
    if (!s.settings.aiAdvice) return;
    if (!dest || s.routes.length < 2 || adviceDismissed) return;

    const input = {
      destination: dest,
      routes: s.routes,
      stats: routeStats,
      trips: await listTrips(60),
      settings: s.settings,
      elevation,
    };
    const key = adviceKey(input);
    if (key === lastAdviceKey) return; // same question, already answered
    lastAdviceKey = key;

    adviceAbort?.abort();
    const requestController = new AbortController();
    adviceAbort = requestController;
    adviceLoading = true;
    renderPreview();

    try {
      const result = await fetchAdvice(input, requestController.signal);
      if (requestController.signal.aborted || adviceAbort !== requestController || store.get().destination !== dest || store.get().routes !== s.routes) return; // driver moved on
      advice = result;
    } catch {
      if (adviceAbort !== requestController || requestController.signal.aborted) return;
      advice = null; // never surface advisor failures to a driver
    } finally {
      if (adviceAbort !== requestController) return;
      adviceLoading = false;
      renderPreview();
    }
  }

  /**
   * Frame the whole route while the selector is open.
   *
   * The fit only ran on the first fetch, so picking a different option left the
   * camera where it was and the new route ran off the edge of the screen -
   * which defeats the point of showing alternatives. Re-fitting on every render
   * keeps whatever is selected fully in view.
   */
  function fitPreview(): void {
    const s = store.get();
    const shown = s.routes.length ? s.routes : [];
    if (!shown.length) return;
    const sheet = previewSheetRect();
    mapView.fitRoute(shown, isPortrait(), sheet.h, sheet.w);
  }

  function renderPreview(): void {
    const s = store.get();
    if (!s.destination || s.mode !== 'preview') return;
    // Which sailing each option is planned around; null for ferry-free routes.
    const isTruck = activeVehicle(s.settings).kind === 'truck';
    const ferryPerRoute = s.routes.map((r) =>
      planFerry(r, paceDuration(r), Date.now(), isTruck),
    );
    preview.render({
      destination: s.destination,
      stops: routeStops,
      routes: s.routes,
      stats: routeStats,
      selectedIndex: s.selectedRouteIndex,
      loading: s.routing,
      error: s.routeError,
      isSaved: isDestSaved(),
      advice,
      adviceLoading,
      elevation,
      vehicleName: activeVehicle(s.settings).name,
      originName: plannedOrigin?.name ?? null,
      roughness: routeRough,
      roughPerRoute,
      widthPerRoute,
      ferryPerRoute,
      pttPerRoute,
      clearancePerRoute,
    });
    const selected = s.routes[s.selectedRouteIndex];
    mapView.drawRoute(selected ?? null);
    mapView.drawAlternatives(s.routes.filter((_, i) => i !== s.selectedRouteIndex));
    mapView.drawRouteLetters(s.routes, s.selectedRouteIndex);
    // Measured after the sheet has been laid out, so the padding is right.
    requestAnimationFrame(fitPreview);
  }

  function selectRoute(index: number): void {
    store.set({ selectedRouteIndex: index });
    const picked = store.get().routes[index];
    if (picked) {
      routeRough =
        roughPerRoute[store.get().selectedRouteIndex] ??
        roughnessOfRoute(picked, roughIndex, store.get().settings.roughSensitivity);
    }
    elevation = null;
    renderPreview();
    void refreshElevation();
  }

  /**
   * Work out how hard the selected route is to climb, for THIS vehicle.
   * Runs after the routes are already on screen: it costs a handful of terrain
   * tiles and must never delay the driver seeing their options.
   */
  async function refreshElevation(): Promise<void> {
    const s = store.get();
    const route = s.routes[s.selectedRouteIndex];
    if (!route) return;
    const forRoute = route._id;
    try {
      const profile = await analyseElevation(route, activeVehicle(s.settings));
      // The driver may have picked a different route while we were fetching.
      if (store.get().routes[store.get().selectedRouteIndex]?._id !== forRoute) return;
      elevation = profile;
      renderPreview();
      routeInfo.refresh();
      void requestAdvice();
    } catch {
      /* terrain unavailable - the route list simply shows no gradient note */
    }
  }

  function activeRoute(): Route | null {
    const s = store.get();
    return s.routes[s.selectedRouteIndex] ?? null;
  }

  function cancelPreview(): void {
    const restoreSearch = returnToSearchResults;
    returnToSearchResults = false;
    routeInfo.setVisible(false);
    plannedOrigin = null;
    searchTarget = 'destination';
    routeAbort?.abort();
    preview.hide();
    mapView.drawRoute(null);
    mapView.drawAlternatives([]);
    mapView.drawRouteLetters([], 0);
    mapView.setDestination(null);
    routeStops = [];
    routeWaypointKinds = [];
    stopProgressRoute = null;
    mapView.setStops([]);
    store.set({ mode: 'idle', destination: null, routes: [], routeError: null, routing: false });
    const f = store.get().fix;
    if (f) mapView.jumpTo([f.lng, f.lat], 15.5);
    if (restoreSearch) setPanel('search', true);
  }

  /* ----------------------------- navigation ---------------------------- */
  let navigator_: RouteNavigator | null = null;
  function remainingRouteStops(): Place[] {
    if (!navigator_ || store.get().mode !== 'navigating') return [...routeStops];
    const progress = store.get().progress;
    // A replacement route is live before its first GPS update. Do not apply
    // the previous route's leg index to the new waypoint list in that gap.
    if (!progress || stopProgressRoute !== navigator_.route) return [...routeStops];
    const passedUsers = routeWaypointKinds
      .slice(0, Math.max(0, progress.legIndex))
      .filter((kind) => kind === 'user').length;
    return routeStops.slice(passedUsers);
  }

  function setNavigatingStops(
    stops: Place[],
    kinds: RouteWaypointKind[] = stops.map(() => 'user'),
  ): void {
    routeStops = stops.map((stop) => ({ ...stop, center: [...stop.center] as LngLat }));
    routeWaypointKinds = [...kinds];
    stopProgressRoute = null;
    mapView.setStops(routeStops.map((stop) => stop.center));
  }

  /** AI junction guidance for the route being driven. See ai/laneAdvisor.ts. */
  let laneGuides: LaneGuideCache | null = null;
  function resetLaneGuides(route: Route): void {
    const next = new LaneGuideCache();
    laneGuides = next;
    void next.prefetch(
      route.legs.flatMap((l) => l.steps),
      getLang(),
      route.legs[0]?.steps[0]?.driving_side ?? 'left',
    );
  }
  let rerouteInFlight = false;
  let lastRerouteAt = 0;
  let lastRerouteAnnounceAt = 0;
  let recorder: TripRecorder | null = null;

  /* Road-surface knowledge accumulated from our own drives. */
  const roughMeter = new RoughnessMeter();
  /* Vehicle-condition metrics from the same sensor; see nav/vehicleHealth.ts. */
  const healthMonitor = new VehicleHealthMonitor();

  /*
   * Per-user dotted trail of the last 500 km driven. Fed from EVERY fix, not only while
   * navigating - "where have we been" doesn't care whether a destination was
   * set. Redraws are throttled: a dot every 30 m does not need a map update
   * per fix.
   */
  const breadcrumbs = new BreadcrumbTrail(currentUser!.id, () => redrawCrumbs(true));
  let crumbsDrawnAt = 0;
  function redrawCrumbs(force = false): void {
    if (!store.get().settings.breadcrumbTrail) return;
    const now = Date.now();
    if (!force && now - crumbsDrawnAt < 15_000) return;
    crumbsDrawnAt = now;
    mapView.setBreadcrumbs(breadcrumbs.lines());
  }
  redrawCrumbs(true);
  select(
    (st) => st.settings.breadcrumbTrail,
    (on) => (on ? redrawCrumbs(true) : mapView.clearBreadcrumbs()),
    { immediate: false },
  );

  let roughIndex: RoughIndex = { cells: new Map(), count: 0 };
  let routeRough: RouteRoughness | null = null;
  let roughPerRoute: Array<RouteRoughness | null> = [];
  let widthPerRoute: Array<RoadWidthProfile | null> = [];
  let pttPerRoute: Array<PttGapStats | null> = [];
  let clearancePerRoute: Array<RouteClearance | null> = [];
  let warnedRoughAt = -1;

  async function refreshRoughIndex(): Promise<void> {
    try {
      const trips = await listTrips(120);
      roughIndex = buildRoughIndex(trips);
      await refreshDrivenOverlay(trips);
    } catch {
      /* no history yet - the overlay and warnings simply stay quiet */
    }
  }

  /** Redraw the "roads we've driven" overlay in whichever mode is selected. */
  async function refreshDrivenOverlay(preloaded?: Awaited<ReturnType<typeof listTrips>>): Promise<void> {
    const mode = store.get().settings.drivenOverlay;
    if (mode === 'off') {
      mapView.clearDrivenTracks();
      return;
    }
    const trips = preloaded ?? (await listTrips(120));
    const tracks = trips.map((trip) =>
      (trip.track ?? [])
        .map((raw) => {
          const p = readPoint(raw);
          // Third value is whatever the overlay is colouring by.
          return [p[0], p[1], mode === 'rough' ? p[6] : p[3]] as [number, number, number];
        })
        // Thin long tracks: at overlay scale every third point is plenty.
        .filter((_, i) => i % 3 === 0),
    );
    mapView.setDrivenTracks(tracks, mode === 'rough' ? 'rough' : 'speed');
  }

  void refreshRoughIndex();
  select(
    (st) => st.settings.drivenOverlay,
    () => void refreshDrivenOverlay(),
    { immediate: false },
  );
  let routeCumulative: number[] = [];

  function startNavigation(publish = true): void {
    const route = activeRoute();
    const dest = store.get().destination;
    if (!route || !dest) return;
    const vehicle = activeVehicle(store.get().settings);
    if (route._vehicleProfileKey !== vehicleProfileKey(store.get().settings)) {
      void refetchPreviewRoutes(); return;
    }
    const clearance = vehicle.kind === 'truck' && vehicle.height ? getRouteClearance(route, vehicle.height) : null;
    if (vehicleViolation(route, store.get().settings) || clearance?.limits.some(limit => limit.blocked)) {
      statusFlash('This route is blocked for the selected vehicle. Choose another route.'); return;
    }
    if (vehicle.kind === 'truck' && !clearance) {
      statusFlash('Checking truck height restrictions before starting.');
      void refreshClearances(store.get().routes, new AbortController().signal); return;
    }

    // A route planned from somewhere else cannot be driven turn-by-turn from
    // here. Drop the planned origin and re-route from the vehicle's actual
    // position; the driver sees the corrected route immediately.
    const fixNow = currentFixLngLat();
    if (plannedOrigin && fixNow && distance(plannedOrigin.center, fixNow) > 250) {
      plannedOrigin = null;
      void refetchPreviewRoutes();
      return;
    }
    plannedOrigin = null;
    rememberActiveTrip(dest, routeStops);
    if (publish) publishUserRoute(dest, 'navigating');

    primeSpeech();
    // Motion access on iOS must be asked for from a user gesture; this is one.
    // The health monitor listens to the same devicemotion stream, so one grant
    // covers both.
    void RoughnessMeter.requestPermission().then((ok) => {
      if (ok) {
        roughMeter.start();
        healthMonitor.start(activeVehicle(store.get().settings).kind);
      }
    });
    // A history track left on the map would be indistinguishable from the
    // route at speed; reviewing ends when driving begins.
    clearTripView();
    preview.hide();
    mapView.drawAlternatives([]);
    mapView.drawRouteLetters([], 0);
    mapView.drawRoute(route);
    mapView.startNavigation();
    hud.show();
    appEl.dataset.mode = 'navigating';
    store.set({ mode: 'navigating' });
    routeInfo.setVisible(true);
    livePanel.setVisible(true);
    askAi.setVisible(true);
    hotelSheet.setVisible(true);
    quickSettings.setVisible(true);
    liveSamples = 0;
    liveMovingSeconds = 0;
    lastFixAt = 0;
    routeCumulative = cumulativeDistances(route.geometry.coordinates);
    fleet.setDestinationName(dest.name);
    fuelStops = [];
    allFuel = [];
    fuelSearchedAt = -Infinity;
    routeBeforeDetour = null;
    routeBeforeDetourStops = [];
    routeBeforeDetourWaypointKinds = [];
    routeWaypointKinds = routeStops.map(() => 'user');
    stopProgressRoute = null;
    startTrafficWatch();

    // Where this trip began, so the driver can turn round and come back
    // without hunting for the address again.
    const startFix = store.get().fix;
    if (startFix) {
      tripOrigin = {
        id: `origin-${Date.now().toString(36)}`,
        // Placeholder until the reverse geocode below names it properly.
        name: t('nav.backToStart'),
        address: '',
        kind: 'place',
        center: [startFix.lng, startFix.lat] as LngLat,
      };
      void reverseGeocode([startFix.lng, startFix.lat])
        .then((place) => {
          // Keep the coordinates we actually started from; borrow only the name.
          const current: Place | null = tripOrigin;
          if (current && place.name) {
            tripOrigin = { ...current, name: place.name, address: place.address };
          }
          refreshBackButton();
        })
        .catch(() => {});
      refreshBackButton();
    }

    /*
     * AI junction guidance for this route.
     *
     * Prefetched in bounded batches at departure so it is in hand at speed,
     * with no latency at the junction and no dependence on signal there. A new
     * cache per route, never reused across a reroute: the indices belong to a
     * specific step list. Entirely best-effort - see ai/laneAdvisor.ts.
     */
    resetLaneGuides(route);

    navigator_ = new RouteNavigator(
      route,
      {
        // Turn instructions are the definition of "something needs doing", so
        // they still sound in alerts-only mode - as a chime.
        onAnnounce: (text: string) => announce(text, 'action'),
        onOffRoute: () => void handleOffRoute(),
        onArrive: () => endNavigation('arrived'),
      },
      // Announcements are composed by the navigator, so it needs to know which
      // language and units to compose them in.
      () => ({
        units: store.get().settings.units,
        lang: getLang(),
        guideFor: (i: number) => laneGuides?.get(i) ?? null,
      }),
    );

    const s = store.get().settings;
    if (s.logTrips) {
      const f = store.get().fix;
      const stats = routeStats[store.get().selectedRouteIndex] ?? computeRouteStats(route);
      recorder = new TripRecorder({
        destinationName: dest.name,
        destination: dest.center,
        originName: 'Current location',
        origin: f ? [f.lng, f.lat] : null,
        vehicle: activeVehicle(s),
        predictedDuration: route.duration,
        predictedDistance: route.distance,
        trafficSignals: stats.trafficSignals,
      });
    }

    announce(t('nav.voice.starting', { name: dest.name }), 'action');
  }

  function onNavFix(fix: Fix): void {
    if (!navigator_) return;
    const active = navigator_;
    const progress = active.update(fix);

    /*
     * `update()` fires onArrive synchronously, and onArrive tears the whole
     * navigation down. Everything below this line would then run against a
     * session that no longer exists: re-showing the maneuver card, writing
     * into a hidden HUD, restarting the camera - leaving the app in idle mode
     * with a half-live navigation UI and no way back short of restarting it.
     * That is the "bottom card disappears when we get to our destination"
     * report. If teardown happened, we are done.
     */
    if (navigator_ !== active) return;

    stopProgressRoute = active.route;
    store.set({ progress });
    if (!progress.offRoute) {
      mapView.drawRouteAhead(
        active.route,
        progress.snapped.segmentIndex,
        progress.snapped.location,
      );
    }

    const s = store.get().settings;
    maneuverCard.update(progress, s.units, fix.speed ?? 0);
    /*
     * The next-turn card now sits to the RIGHT of the main sign, leaving the
     * vehicle and its lane arrows on the map centreline.
     */
    if (dashboardMode) {
      mapView.setCameraLeftInset(0);
      /*
       * Lift the vehicle above everything anchored to the bottom edge. The
       * lane sign sits immediately above the dock, so measuring only the dock
       * placed the puck behind the arrows on short head units.
       */
      const dockH = carDock ? Math.round(carDock.el.getBoundingClientRect().height) : 0;
      const laneH = maneuverCard.laneEl.hidden
        ? 0
        : Math.ceil(maneuverCard.laneEl.getBoundingClientRect().height);
      const carGutter = Number.parseFloat(
        getComputedStyle(appEl).getPropertyValue('--car-gutter'),
      ) || 0;
      mapView.setCameraBottomInset(dockH + (laneH > 0 ? laneH + carGutter : 0));
      const mapTop = mapEl.getBoundingClientRect().top;
      const clearTop = Math.max(
        maneuverCard.el.hidden ? 0 : maneuverCard.el.getBoundingClientRect().bottom,
        !detachedThenEl || detachedThenEl.hidden ? 0 : detachedThenEl.getBoundingClientRect().bottom,
      );
      mapView.setCameraTopInset(Math.max(0, clearTop - mapTop));
    }
    refineGuideIfBlank(progress);
    if (s.adaptiveCamera || mapView.isFollowing) mapView.driveCamera(fix, progress);
    else mapView.updatePuck(fix, progress.snapped.location);

    hud.update(progress, fix.speed, s.units);
    routeInfo.refresh();

    // Feed the speed BEFORE reading, or the reading is judged against the
    // previous fix - which is how a reading survived the moment of stopping.
    roughMeter.setSpeed((fix.speed ?? 0) * 3.6);
    healthMonitor.setSpeed((fix.speed ?? 0) * 3.6);
    liveRough = roughMeter.read();

    // Only count samples the vehicle actually earned. Counting every fix meant
    // the surface counter climbed steadily while parked at a red light, which
    // is neither a distance driven nor a measurement of any road.
    if (liveRough != null) liveSamples++;
    if (lastFixAt && (fix.speed ?? 0) > 1) {
      liveMovingSeconds += Math.min(30, (fix.timestamp - lastFixAt) / 1000);
    }
    lastFixAt = fix.timestamp;
    livePanel.refresh();

    // Live warning before a stretch we already know is badly broken.
    if (routeRough?.known) {
      const ahead = roughAhead(routeRough, progress.snapped.distanceAlongRoute, 900);
      if (ahead && ahead.from !== warnedRoughAt) {
        warnedRoughAt = ahead.from;
        const away = Math.max(0, ahead.from - progress.snapped.distanceAlongRoute);
        hud.toast(t('rough.aheadToast', { d: formatDistanceShort(away, s.units) }));
        announce(t('rough.aheadVoice'), 'action');
      }
    }

    // Next PTT along the route, and how far to it.
    void refreshFuelStop(progress.snapped.distanceAlongRoute);
    if ((fuelStops.length || allFuel.length) && navigator_) {
      const at = progress.snapped.distanceAlongRoute;
      const route = navigator_.route;
      const toChoice = (stop: FuelStop): FuelChoice => ({
        stop,
        remaining: Math.max(0, stop.along - at),
        eta: etaToStop(route, stop, at),
      });
      // null: there is no other-brand alternative to offer any more.
      fuelBubble.update(fuelStops.map(toChoice), null, allFuel.map(toChoice), s.units);
    } else {
      fuelBubble.update([], null, [], s.units);
    }

    // Trip log: the recorder handles its own sampling cadence and rolls the
    // drive up into segments as it goes.
    recorder?.add(fix, roadLabel(progress.step, getLang()), tierForStep(progress.step), roughMeter.read());
  }

  async function handleOffRoute(): Promise<void> {
    if (rerouteInFlight || !navigator_) return;
    // Cooldown: repeated triggers (bad GPS patches) must not hammer the
    // routing API or chant "route updated" at the driver.
    if (Date.now() - lastRerouteAt < 20_000) return;
    const dest = store.get().destination;
    const fix = store.get().fix;
    if (!dest || !fix) return;
    const remainingStops = remainingRouteStops();
    const basedOnRoute = navigator_.route;

    lastRerouteAt = Date.now();
    rerouteInFlight = true;
    store.set({ rerouting: true });
    hud.toast(t('nav.rerouting'), true);

    try {
      // Compare every permitted alternative after a wrong turn, fastest first.
      const routes = await fetchRoutes({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        via: remainingStops.map((stop) => stop.center),
        heading: fix.heading,
        settings: { ...store.get().settings, route: { ...store.get().settings.route, alternatives: true } },
      });
      const rs = store.get().settings;
      const route = fastestRouteOrder(applyPace(routes, rs.speeds, rs.signalSeconds), activeVehicle(rs).kind === 'truck')[0];
      if (!route) throw new Error('No route found');
      if (!(await truckRouteAllowed(route))) return;
      if (!navigator_ || navigator_.route !== basedOnRoute || store.get().destination !== dest || store.get().mode !== 'navigating') return;
      setNavigatingStops(remainingStops);
      navigator_.replaceRoute(route);
      resetLaneGuides(route);
      routeCumulative = cumulativeDistances(route.geometry.coordinates);
      routeStats = [computeRouteStats(route)];
      recorder?.noteReroute();
      store.set({ routes: [route], selectedRouteIndex: 0 });
      mapView.drawRoute(route);
      rememberActiveTrip(dest, routeStops);
      void scanManeuverStructures([route]).catch(() => {});
      hud.clearToast();
      if (Date.now() - lastRerouteAnnounceAt > 45_000) {
        lastRerouteAnnounceAt = Date.now();
        announce(t('nav.voice.updated'));
      }
    } catch {
      if (navigator_?.route !== basedOnRoute || store.get().destination !== dest || store.get().mode !== 'navigating') return;
      hud.toast(store.get().online ? t('nav.rerouteFailed') : t('nav.rerouteOffline'));
    } finally {
      rerouteInFlight = false;
      store.set({ rerouting: false });
    }
  }

  /* --------------------------- traffic watch ---------------------------- */
  /*
   * Every few minutes of navigation, quietly re-ask the router from the live
   * position. When real congestion sits ahead on the driven route AND a
   * fairly-easy alternative saves real time, offer the switch as a one-tap
   * toast - never reroute automatically. Decision rules in nav/trafficWatch.
   */
  let trafficTimer: number | null = null;
  let lastTrafficPromptAt = 0;
  let trafficCheckBusy = false;

  function startTrafficWatch(): void {
    stopTrafficWatch();
    lastTrafficPromptAt = 0;
    trafficTimer = window.setInterval(() => void checkTrafficAhead(), 240_000);
  }

  function stopTrafficWatch(): void {
    if (trafficTimer !== null) clearInterval(trafficTimer);
    trafficTimer = null;
  }

  async function checkTrafficAhead(): Promise<void> {
    const st = store.get();
    if (st.mode !== 'navigating' || !st.settings.trafficWatch || !st.online) return;
    if (!navigator_ || rerouteInFlight || trafficCheckBusy) return;
    const fix = st.fix;
    const dest = st.destination;
    const progress = st.progress;
    if (!fix || !dest || !progress) return;
    // Close to arrival there is nothing worth switching for; and don't nag -
    // one offer per ten minutes, taken or not.
    if (progress.distanceRemaining < 8_000) return;
    if (Date.now() - lastTrafficPromptAt < 10 * 60_000) return;
    const remainingStops = remainingRouteStops();
    const basedOnRoute = navigator_.route;

    trafficCheckBusy = true;
    try {
      const fetched = await fetchRouteAlternatives({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        via: remainingStops.map((stop) => stop.center),
        heading: fix.heading,
        settings: { ...st.settings, route: { ...st.settings.route, alternatives: true } },
      });
      const s2 = store.get();
      if (s2.mode !== 'navigating' || !navigator_ || navigator_.route !== basedOnRoute || s2.destination !== dest) return;
      const fresh = applyPace(fetched, s2.settings.speeds, s2.settings.signalSeconds);

      const samples = sampleRemaining(
        navigator_.route.geometry.coordinates,
        routeCumulative,
        s2.progress?.snapped.distanceAlongRoute ?? 0,
      );
      const advice = adviseOnTraffic(fresh, samples, computeRouteStats);
      if (!advice) return;

      lastTrafficPromptAt = Date.now();
      const min = Math.max(1, Math.round(advice.savesSeconds / 60));
      const alt = advice.alternative;
      const stayStats = computeRouteStats(advice.stay);
      const altStats = computeRouteStats(alt);
      if (advice.reason === 'traffic') announce(t('traffic.voice', { min }), 'action');
      routeOffer.show({
        reason: advice.reason,
        routeName: summariseRoute(alt),
        savesMinutes: advice.savesSeconds / 60,
        currentDelayMinutes: advice.delaySeconds / 60,
        distanceDeltaKm: (alt.distance - advice.stay.distance) / 1000,
        trafficDelaySavedMinutes: (trafficDelay(advice.stay) - trafficDelay(alt)) / 60,
        congestionDropPoints: (congestedShare(advice.stay) - congestedShare(alt)) * 100,
        fastRoadGainMinutes: (fastRoadSeconds(alt) - fastRoadSeconds(advice.stay)) / 60,
        motorwayGainMinutes: (motorwaySeconds(alt) - motorwaySeconds(advice.stay)) / 60,
        turnsSaved: stayStats.turns - altStats.turns,
      }, {
        onAccept: () => {
          if (navigator_?.route === basedOnRoute && store.get().destination === dest && store.get().mode === 'navigating') switchToRoute(alt, remainingStops);
        },
        onDecline: () => {},
      });
    } catch {
      /* offline blip or router error - the next tick simply tries again */
    } finally {
      trafficCheckBusy = false;
    }
  }

  /**
   * Ask the model, right now, whether to change route.
   *
   * Deliberately NOT rate-limited the way the watcher is: the driver pressed a
   * button and must get an answer. The server's per-user daily cap is the real
   * guard against runaway cost, and it reports 429 which surfaces as a plain
   * message rather than silence.
   */
  async function askAboutRoute(question: string | null): Promise<void> {
    const st = store.get();
    const dest = st.destination;
    const fix = st.fix;
    const progress = st.progress;

    if (st.mode !== 'navigating' || !dest || !fix || !navigator_) {
      askAi.fail(t('ask.notNavigating'));
      return;
    }
    if (!st.online) {
      askAi.fail(t('ask.offline'));
      return;
    }

    try {
      const remainingStops = remainingRouteStops();
      askRouteStops = remainingStops;
      // Fresh options from where the vehicle actually is - the routes in the
      // store are from departure and say nothing about the road ahead now.
      const fetched = await fetchRouteAlternatives({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        via: remainingStops.map((stop) => stop.center),
        heading: fix.heading,
        settings: { ...st.settings, route: { ...st.settings.route, alternatives: true } },
      });
      const s2 = store.get();
      if (s2.mode !== 'navigating' || !navigator_) return;

      const fresh = applyPace(fetched, s2.settings.speeds, s2.settings.signalSeconds);
      const stats = fresh.map((r) => computeRouteStats(r));

      // Which of the fresh options is the one being driven? Same geometric
      // overlap the watcher uses - the router does not label them.
      const samples = sampleRemaining(
        navigator_.route.geometry.coordinates,
        routeCumulative,
        s2.progress?.snapped.distanceAlongRoute ?? 0,
      );
      let currentIndex = -1;
      let bestOverlap = 0.6; // below this it is not really "the same route"
      fresh.forEach((r, i) => {
        const o = overlapShare(samples, r);
        if (o > bestOverlap) {
          bestOverlap = o;
          currentIndex = i;
        }
      });

      const current = currentIndex >= 0 ? fresh[currentIndex] : navigator_.route;
      const advice = await fetchAdvice({
        destination: dest,
        routes: fresh,
        stats,
        trips: await listTrips(60),
        settings: s2.settings,
        elevation,
        live: {
          currentIndex,
          minutesRemaining: (progress?.durationRemaining ?? 0) / 60,
          kmRemaining: (progress?.distanceRemaining ?? 0) / 1000,
          delayMin: trafficDelay(current) / 60,
          congestedShare: congestedShare(current),
          question,
        },
      });

      if (!advice) {
        askAi.fail(t('ask.noAnswer'));
        return;
      }
      askAi.show(advice, fresh, currentIndex);
    } catch {
      askAi.fail(t('ask.failed'));
    }
  }

  async function truckRouteAllowed(route: Route): Promise<boolean> {
    const settings = store.get().settings;
    const key = vehicleProfileKey(settings);
    if (route._vehicleProfileKey !== key || vehicleViolation(route, store.get().settings)) return false;
    const vehicle = activeVehicle(settings);
    if (vehicle.kind !== 'truck') return true;
    if (!vehicle.height) return false;
    const clearance = getRouteClearance(route, vehicle.height) ?? (await scanRouteClearances([route], vehicle.height))[0];
    if (vehicleProfileKey(store.get().settings) !== key) return false;
    const blocked = clearance.limits.find(limit => limit.blocked);
    if (blocked) {
      hud.toast(`Route blocked: ${blocked.clearanceM.toFixed(1)} m clearance for a ${vehicle.height.toFixed(1)} m truck.`);
      announce('Low bridge on proposed route. Route change blocked.', 'action');
      return false;
    }
    if (!clearance.checked) hud.toast('Truck clearance unverified. Check posted bridge limits.');
    return true;
  }

  /** Apply an accepted traffic-avoidance route mid-navigation. */
  async function switchToRoute(route: Route, stops: Place[] = remainingRouteStops()): Promise<void> {
    const basedOnRoute = navigator_?.route;
    if (!(await truckRouteAllowed(route)) || navigator_?.route !== basedOnRoute) return;
    if (!navigator_ || store.get().mode !== 'navigating') return;
    routeOffer.hide();
    setNavigatingStops(stops);
    navigator_.replaceRoute(route);
    resetLaneGuides(route);
    routeCumulative = cumulativeDistances(route.geometry.coordinates);
    routeStats = [computeRouteStats(route)];
    routeRough = roughnessOfRoute(route, roughIndex, store.get().settings.roughSensitivity);
    recorder?.noteReroute();
    routeBeforeDetour = null;
    routeBeforeDetourStops = [];
    routeBeforeDetourWaypointKinds = [];
    fuelStops = [];
    allFuel = [];
    fuelSearchedAt = -Infinity;
    store.set({ routes: [route], selectedRouteIndex: 0 });
    mapView.drawRoute(route);
    const destination = store.get().destination;
    if (destination) rememberActiveTrip(destination, routeStops);
    void scanManeuverStructures([route]).catch(() => {});
    hud.clearToast();
    hud.toast(t('traffic.switched'));
    announce(t('nav.voice.updated'));
  }

  function endNavigation(reason: 'arrived' | 'ended'): void {
    routeOffer.hide();
    const dest = store.get().destination;
    const endingRoutePlanId = currentRoutePlanId;
    currentRoutePlanId = null;
    if (dest && endingRoutePlanId) {
      publishUserRoute(dest, reason, endingRoutePlanId);
      // publishUserRoute assigns the id for retries; the route is no longer
      // current locally even though the finished record remains on the server.
      currentRoutePlanId = null;
    }
    clearActiveTrip();
    if (reason === 'arrived' && dest) {
      announce(t('nav.voice.arrived', { name: dest.name }), 'action');
      hud.toast(t('nav.arrivedToast'));
    } else {
      stopSpeaking();
    }

    if (recorder) {
      // Never let a failure here strand the UI mid-teardown - the rest of this
      // function is what returns the app to a usable state.
      try {
        const done = recorder.finish(activeVehicle(store.get().settings));
        if (done) {
          // Keep the accelerometer trend in trip history, but do not interrupt
          // the driver with a speculative road/vehicle warning after arrival.
          const health = healthMonitor.finish();
          if (health) done.health = health;
          void saveTrip(done);
          void authApi.syncTrip(done).catch(() => {});
        }
      } catch (err) {
        console.warn('[trip] could not finish recording', err);
      }
      recorder = null;
    }

    navigator_ = null;
    stopTrafficWatch();
    roughMeter.stop();
    healthMonitor.stop();
    routeRough = null;
    warnedRoughAt = -1;
    void refreshRoughIndex();
    maneuverCard.hide();
    fuelBubble.hide();
    fuelStops = [];
    allFuel = [];
    routeBeforeDetour = null;
    routeBeforeDetourStops = [];
    routeBeforeDetourWaypointKinds = [];
    routeWaypointKinds = [];
    stopProgressRoute = null;
    askRouteStops = [];
    fleet.setDestinationName(null);
    hud.hide();
    appEl.dataset.mode = 'idle';
    if (dashboardMode) {
      // The detached lane sign has just disappeared. Retain clearance for the
      // persistent car dock, but do not leave its old lane height in the idle
      // camera or the vehicle stays unnecessarily high after arrival.
      const dockH = carDock ? Math.round(carDock.el.getBoundingClientRect().height) : 0;
      mapView.setCameraBottomInset(dockH);
      mapView.setCameraTopInset(0);
    }
    mapView.stopNavigation();
    mapView.drawRoute(null);
    mapView.setDestination(null);
    routeStops = [];
    mapView.setStops([]);
    routeInfo.setVisible(false);
    livePanel.setVisible(false);
    askAi.setVisible(false);
    hotelSheet.setVisible(false);
    quickSettings.setVisible(
      !dashboardMode &&
        window.matchMedia('(max-width: 900px), (orientation: landscape) and (max-height: 620px)').matches,
    );
    clearHotelCache();
    store.set({ mode: 'idle', destination: null, routes: [], progress: null });
    refreshBackButton();
    refreshRecenter();
    const f = store.get().fix;
    if (f) mapView.jumpTo([f.lng, f.lat], 15.5);
  }

  async function acceptSyncedRoute(route: authApi.UserRoute): Promise<void> {
    currentRoutePlanId = route.id;
    lastSyncedRouteAt = route.updatedAt;
    pendingSyncedRoute = null;

    const sameDestination =
      store.get().destination?.center[0] === route.destination.center[0] &&
      store.get().destination?.center[1] === route.destination.center[1];
    const incomingStops = route.stops ?? [];
    const sameStops =
      routeStops.length === incomingStops.length &&
      routeStops.every(
        (stop, index) =>
          stop.center[0] === incomingStops[index]?.center[0] &&
          stop.center[1] === incomingStops[index]?.center[1],
      );
    if (!sameDestination || !sameStops || store.get().mode === 'idle' || store.get().mode === 'searching') {
      await pickDestination(route.destination, false, false, incomingStops);
    }
    if (route.status === 'navigating' && store.get().mode === 'preview' && store.get().routes.length) {
      startNavigation(false);
    }
  }

  /**
   * The head unit watches only the signed-in user's active route. A new route
   * may replace an idle/preview screen automatically; it never interrupts an
   * active drive without a deliberate tap on the HUD action.
   */
  async function pollUserRoute(): Promise<void> {
    if (!carMode || routeSyncBusy || document.hidden || !store.get().online) return;
    routeSyncBusy = true;
    try {
      const { active } = await authApi.fetchUserRoutes(true);
      if (!active || active.sourceDevice === routeSyncDevice || active.updatedAt <= lastSyncedRouteAt) return;
      lastSyncedRouteAt = active.updatedAt;

      if (store.get().mode === 'navigating' && active.id !== currentRoutePlanId) {
        pendingSyncedRoute = active;
        hud.toast(t('routeSync.new'), true, {
          label: t('routeSync.use'),
          onAction: () => {
            const pending = pendingSyncedRoute;
            if (!pending) return;
            // End the local recorder cleanly before calculating the replacement
            // route; accepting the toast is the driver's explicit hand-off.
            endNavigation('ended');
            void acceptSyncedRoute(pending);
          },
        });
        return;
      }

      await acceptSyncedRoute(active);
    } catch {
      // Mobile data blips are normal. The next two-second poll retries without
      // putting an error over navigation.
    } finally {
      routeSyncBusy = false;
    }
  }

  if (carMode) {
    window.setInterval(() => void pollUserRoute(), ROUTE_SYNC_POLL_MS);
    window.setTimeout(() => void pollUserRoute(), 600);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) void pollUserRoute();
    });
  }

  // ETA formatting reads this, so keep it in step with the setting.
  setClock24(store.get().settings.clock24);
  store.subscribe(() => setClock24(store.get().settings.clock24));

  /* ------------------------------ version tag --------------------------- */
  const versionEl = document.createElement('div');
  versionEl.className = 'version-tag';
  versionEl.textContent = `v${APP_VERSION}`;
  uiEl.appendChild(versionEl);
  void showNativeUpdateIfAvailable(uiEl);

  /* ------------------------- viewport plumbing -------------------------- */
  /** Measured size of the preview sheet, so the route is fitted clear of it. */
  function previewSheetRect(): { w: number; h: number } {
    if (preview.el.hidden) return { w: 0, h: 0 };
    const r = preview.el.getBoundingClientRect();
    return { w: Math.round(r.width), h: Math.round(r.height) };
  }

  function isPortrait(): boolean {
    return window.innerHeight >= window.innerWidth;
  }
  window.addEventListener('resize', () => mapView.resize());
  window.addEventListener('orientationchange', () => setTimeout(() => mapView.resize(), 250));

  /*
   * Dev-only: ?simnav=<lat>,<lng> plans a route to that point and starts
   * navigating, so the navigating UI - the /car top bar above all - is on
   * screen at load instead of after a manual search-and-Go every reload.
   *
   * Pair it with ?simgps (dev-geo.ts) so the vehicle is actually moving on
   * the route. Without a fix ON the route there is no progress to render and
   * the maneuver card stays empty, which looks exactly like a broken top bar.
   *
   * import.meta.env.DEV is statically false in a build, so this whole block
   * is dropped by the bundler and cannot ship.
   */
  select(st => vehicleProfileKey(st.settings), () => {
    if (store.get().mode !== 'navigating') return;
    const destination = store.get().destination;
    const stops = remainingRouteStops();
    endNavigation('ended');
    if (destination) void pickDestination(destination, true, false, stops);
  }, { immediate: false });

  if (import.meta.env.DEV) {
    const devParams = new URLSearchParams(location.search);
    const junctionDemo = devParams.get('junctionDemo');
    if (junctionDemo) {
      const side = junctionDemo === 'left' ? 'left' : 'right';
      const count = junctionDemo === 'many' ? 8 : 5;
      const demoLanes = junctionDemo === 'missing' ? null : Array.from({ length: count }, (_, i) => {
        const valid = side === 'left' ? i < 2 : i >= count - 2;
        return { valid, indications: [valid ? `slight ${side}` as const : 'straight' as const] };
      });
      const approach: RouteStep = {
        distance: 700, duration: 35, name: 'Motorway', ref: '7', driving_side: 'left',
        geometry: { type: 'LineString', coordinates: [[100, 13], [100, 13.005]] },
        maneuver: { type: 'continue', modifier: 'straight', location: [100, 13], bearing_before: 0, bearing_after: 0 },
      };
      const turn: RouteStep = { ...approach, name: 'Exit road', ref: '361', exits: '12',
        maneuver: { type: 'off ramp', modifier: `slight ${side}`, location: [100, 13.005], bearing_before: 0, bearing_after: side === 'left' ? 325 : 35 } };
      const progress: NavProgress = {
        legIndex: 0, stepIndex: 0, step: approach, approachStep: approach, maneuverStep: turn, nextStep: null,
        snapped: { location: [100, 13], deviation: 0, segmentIndex: 0, distanceAlongRoute: 0, courseOnRoute: 0 },
        distanceRemaining: 18600, durationRemaining: 1080, distanceToManeuver: 450, offRoute: false,
        lanes: demoLanes, laneAdvice: laneAdvice(demoLanes, 'left', `slight ${side}`), divergence: null, fork: null,
        aiGuide: null, drivingSide: 'left', banner: null,
      };
      appEl.dataset.mode = 'navigating';
      store.set({ mode: 'navigating' });
      const update = () => maneuverCard.update(progress, store.get().settings.units, 22);
      update();
      hud.show();
      hud.update(progress, 22, store.get().settings.units);
      Object.assign(window, { __junctionFixture: { progress, update, store, mapView,
        pickDestination: (place: Place) => {
          plannedOrigin = { id: 'fixture-origin', name: 'Example origin', address: '', center: [100.5, 13.7], kind: 'dropped-pin' };
          return pickDestination(place, false);
        }, cancelPreview,
      } });
      const exampleFuel = ['Shell', 'Bangchak', 'Caltex', 'PT', 'Susco'].map((brand, i): FuelChoice => ({
        stop: { id: `demo-${i}`, name: `${brand} example station`, brand, location: [100, 13],
          along: (i + 1) * 2400, offset: 20, side: 'left', access: 'direct' }, remaining: (i + 1) * 2400, eta: (i + 1) * 140,
      }));
      const examplePtt: FuelChoice = { ...exampleFuel[0], remaining: 7200,
        stop: { ...exampleFuel[0].stop, id: 'demo-ptt', brand: 'PTT', name: 'PTT + 7-Eleven example', hasSevenEleven: true, along: 7200 } };
      fuelBubble.update([examplePtt], null, exampleFuel, store.get().settings.units);
    }
    const simNav = devParams.get('simnav');
    if (simNav) {
      const [lat, lng] = simNav.split(',').map((n) => Number(n.trim()));
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        void (async () => {
          // Wait for the first fix: startNavigation() re-routes from the
          // vehicle's real position and bails out without one.
          for (let i = 0; i < 40 && !store.get().fix; i++) {
            await new Promise((r) => setTimeout(r, 250));
          }
          const place: Place = {
            id: 'dev-simnav',
            name: `Dev destination (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
            // Required and rendered straight into the UI - '' when unknown.
            address: '',
            center: [lng, lat],
            kind: 'dropped-pin',
          };
          await pickDestination(place);
          // pickDestination only fetches the preview; Go is a separate step.
          startNavigation();
        })();
      }
    }

    /*
     * Dev-only visual fixture for the three physically different U-turns.
     * It uses the real ManeuverCard render path, typography and responsive
     * layout; no hand-built screenshot can prove those still fit together.
     */
    const uturnDemo = devParams.get('uturnDemo');
    if (uturnDemo === 'road' || uturnDemo === 'under-bridge' || uturnDemo === 'bridge') {
      const approach: RouteStep = {
        distance: 500,
        duration: 40,
        name: 'Thanon Chaeng Watthana',
        ref: '304',
        driving_side: 'left',
        geometry: { type: 'LineString', coordinates: [[100, 13], [100, 13.001]] },
        maneuver: {
          type: 'continue',
          modifier: 'straight',
          location: [100, 13],
          bearing_before: 0,
          bearing_after: 0,
        },
      };
      const instruction =
        uturnDemo === 'under-bridge'
          ? 'Make a U-turn under the bridge'
          : uturnDemo === 'bridge'
            ? 'Use the U-turn bridge'
            : 'Make a U-turn on road';
      const turn: RouteStep = {
        distance: 900,
        duration: 70,
        name: 'Thanon Liangmueang',
        ref: '48',
        driving_side: 'left',
        geometry: {
          type: 'LineString',
          coordinates: [[100, 13.001], [100.00025, 13.00115], [100.00025, 13.0002]],
        },
        maneuver: {
          type: 'turn',
          modifier: 'uturn',
          location: [100, 13.001],
          bearing_before: 0,
          bearing_after: 180,
          instruction,
        },
      };
      const demoLanes = [
        { valid: false, indications: ['straight' as const] },
        { valid: true, active: true, valid_indication: 'uturn' as const, indications: ['uturn' as const] },
        { valid: true, active: true, valid_indication: 'uturn' as const, indications: ['uturn' as const] },
      ];
      const demoFollowStep: RouteStep | null = devParams.has('thenDemo')
        ? {
            distance: 1_200,
            duration: 90,
            name: 'Rama IX Road',
            ref: '9',
            driving_side: 'left',
            geometry: {
              type: 'LineString',
              coordinates: [[100.00025, 13.0002], [100.001, 13.0002]],
            },
            maneuver: {
              type: 'turn',
              modifier: 'right',
              location: [100.00025, 13.0002],
              bearing_before: 180,
              bearing_after: 270,
              instruction: 'Turn right',
            },
          }
        : null;
      const demoProgress: NavProgress = {
        legIndex: 0,
        stepIndex: 0,
        step: approach,
        approachStep: approach,
        maneuverStep: turn,
        nextStep: demoFollowStep,
        snapped: {
          location: [100, 13],
          deviation: 0,
          segmentIndex: 0,
          distanceAlongRoute: 0,
          courseOnRoute: 0,
        },
        distanceRemaining: 6100,
        durationRemaining: 360,
        distanceToManeuver: 290,
        offRoute: false,
        lanes: demoLanes,
        laneAdvice: {
          lanes: demoLanes,
          total: 3,
          validIndices: [1, 2],
          side: 'right',
          validCount: 2,
          changeRequired: true,
          trap: null,
        },
        divergence: null,
        fork: null,
        aiGuide: null,
        drivingSide: 'left',
        banner: null,
      };
      appEl.dataset.mode = 'navigating';
      store.set({ mode: 'navigating' });
      maneuverCard.update(demoProgress, store.get().settings.units, 16);
      // The real navigation dock always includes the arrival HUD. Keep it in
      // this fixture too, otherwise short-screen overlap checks measure only
      // the media row and miss the production two-row dock.
      hud.show();
      hud.update(demoProgress, 16, store.get().settings.units);
    }

    /* Real-card fixture for an ordinary turn that happens beneath a flyover. */
    const turnDemo = devParams.get('turnDemo');
    if (turnDemo === 'right-under-bridge' || turnDemo === 'left-under-bridge') {
      const side = turnDemo.startsWith('left') ? 'left' : 'right';
      const turnLanes = devParams.has('laneDemo')
        ? side === 'left'
          ? [
              { valid: true, active: true, indications: ['left' as const] },
              { valid: true, active: true, indications: ['left' as const, 'straight' as const] },
              { valid: false, indications: ['straight' as const] },
              { valid: false, indications: ['right' as const] },
            ]
          : [
              { valid: false, indications: ['left' as const] },
              { valid: false, indications: ['straight' as const] },
              { valid: true, active: true, indications: ['straight' as const, 'right' as const] },
              { valid: true, active: true, indications: ['right' as const] },
            ]
        : null;
      const approach: RouteStep = {
        distance: 500,
        duration: 40,
        name: 'Thanon Chaeng Watthana',
        ref: '304',
        driving_side: 'left',
        geometry: { type: 'LineString', coordinates: [[100, 13], [100, 13.001]] },
        maneuver: {
          type: 'continue',
          modifier: 'straight',
          location: [100, 13],
          bearing_before: 0,
          bearing_after: 0,
        },
      };
      const turn: RouteStep = {
        distance: 900,
        duration: 70,
        name: 'Thanon Liangmueang',
        ref: '48',
        driving_side: 'left',
        geometry: {
          type: 'LineString',
          coordinates:
            side === 'right'
              ? [[100, 13.001], [100.00015, 13.001], [100.001, 13.001]]
              : [[100, 13.001], [99.99985, 13.001], [99.999, 13.001]],
        },
        maneuver: {
          type: 'turn',
          modifier: side,
          location: [100, 13.001],
          bearing_before: 0,
          bearing_after: side === 'right' ? 90 : 270,
          instruction: `Turn ${side} under the bridge`,
        },
      };
      const demoProgress: NavProgress = {
        legIndex: 0,
        stepIndex: 0,
        step: approach,
        approachStep: approach,
        maneuverStep: turn,
        nextStep: null,
        snapped: {
          location: [100, 13],
          deviation: 0,
          segmentIndex: 0,
          distanceAlongRoute: 0,
          courseOnRoute: 0,
        },
        distanceRemaining: 6100,
        durationRemaining: 360,
        distanceToManeuver: 290,
        offRoute: false,
        lanes: turnLanes,
        laneAdvice: laneAdvice(turnLanes, 'left', side),
        divergence: null,
        fork: null,
        aiGuide: null,
        drivingSide: 'left',
        banner: null,
      };
      appEl.dataset.mode = 'navigating';
      store.set({ mode: 'navigating' });
      maneuverCard.update(demoProgress, store.get().settings.units, 16);
    }

    /*
     * Dev-only regression fixture for the icon-only false U-turn report.
     * Both cases are joined right+right reversals; only the one that returns
     * to the approach road may consume the second turn as a U-turn.
     */
    const turnPairDemo = devParams.get('turnPairDemo');
    if (turnPairDemo === 'ordinary' || turnPairDemo === 'uturn') {
      const approach: RouteStep = {
        distance: 110,
        duration: 10,
        name: 'Synthetic Avenue',
        ref: 'S1',
        driving_side: 'left',
        geometry: { type: 'LineString', coordinates: [[100, 13], [100, 13.001]] },
        maneuver: {
          type: 'continue',
          modifier: 'straight',
          location: [100, 13],
          bearing_before: 0,
          bearing_after: 0,
        },
      };
      const firstTurn: RouteStep = {
        distance: 32,
        duration: 5,
        name: 'Synthetic Connector',
        driving_side: 'left',
        geometry: {
          type: 'LineString',
          coordinates: [[100, 13.001], [100.0003, 13.001]],
        },
        maneuver: {
          type: 'turn',
          modifier: 'right',
          location: [100, 13.001],
          bearing_before: 0,
          bearing_after: 90,
          instruction: 'Turn right',
        },
      };
      const secondTurn: RouteStep = {
        distance: 300,
        duration: 25,
        name: 'Synthetic Avenue',
        ref: 'S1',
        driving_side: 'left',
        geometry: {
          type: 'LineString',
          coordinates: [[100.0003, 13.001], [100.0003, 13.0002]],
        },
        maneuver: {
          type: 'turn',
          modifier: 'right',
          location: [100.0003, 13.001],
          bearing_before: 90,
          bearing_after: 180,
          instruction: 'Turn right',
        },
      };
      // The ordinary case includes an intervening raw approach step. The card
      // must compare the return road with this exact step, not with the road
      // the driver happens to be on farther back (`step`).
      const maneuverApproach: RouteStep =
        turnPairDemo === 'uturn'
          ? approach
          : {
              ...approach,
              name: 'Synthetic Intermediate Road',
              ref: 'S3',
            };
      const demoProgress: NavProgress = {
        legIndex: 0,
        stepIndex: 0,
        step: approach,
        approachStep: maneuverApproach,
        maneuverStep: firstTurn,
        nextStep: secondTurn,
        snapped: {
          location: [100, 13],
          deviation: 0,
          segmentIndex: 0,
          distanceAlongRoute: 0,
          courseOnRoute: 0,
        },
        distanceRemaining: 6100,
        durationRemaining: 360,
        distanceToManeuver: 290,
        offRoute: false,
        lanes: null,
        laneAdvice: null,
        divergence: null,
        fork: null,
        aiGuide: null,
        drivingSide: 'left',
        banner: null,
      };
      appEl.dataset.mode = 'navigating';
      store.set({ mode: 'navigating' });
      maneuverCard.update(demoProgress, store.get().settings.units, 16);
    }

    /* Selected-route toll markers, rendered by the real Mapbox layers. */
    if (devParams.has('tollDemo')) {
      const tollCoordinates: LngLat[] = [
        [100.545, 13.736],
        [100.555, 13.744],
        [100.565, 13.752],
      ];
      const tollStep: RouteStep = {
        distance: 3_200,
        duration: 180,
        name: 'Sirat Expressway',
        ref: '31',
        driving_side: 'left',
        geometry: { type: 'LineString', coordinates: tollCoordinates },
        maneuver: {
          type: 'depart',
          modifier: 'straight',
          location: tollCoordinates[0],
          bearing_before: 45,
          bearing_after: 45,
        },
        intersections: [
          { location: tollCoordinates[0], geometry_index: 0 },
          {
            location: tollCoordinates[1],
            geometry_index: 1,
            toll_collection: { type: 'toll_booth', name: 'Din Daeng Toll Plaza' },
          },
          {
            location: tollCoordinates[2],
            geometry_index: 2,
            toll_collection: { type: 'toll_gantry', name: 'Easy Pass gantry' },
          },
        ],
      };
      const tollRoute: Route = {
        distance: tollStep.distance,
        duration: tollStep.duration,
        geometry: { type: 'LineString', coordinates: tollCoordinates },
        legs: [{
          distance: tollStep.distance,
          duration: tollStep.duration,
          summary: tollStep.name,
          steps: [tollStep],
        }],
      };
      mapView.drawRoute(tollRoute);
      mapView.fitRoute([tollRoute], isPortrait(), 0, 0);
    }
  }

  trace('done');
  // UI exists from here on - later async errors must not blank the screen.
  traceDone();
}

function bootFailed(error: unknown): void {
  console.error('[startup]', error);
  const detail = String(error instanceof Error ? error.message : error).replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);
  fatalScreen('Wayfarer could not start', `${detail}<br/><small>${traceText()}</small>`);
}

void gate().catch(bootFailed);
